import axios from 'axios';
import { auth as firebaseAuth } from '../firebase';
import { track, EVENTS } from './analytics';

const trackResearchSaved = (kind) => track(EVENTS.RESEARCH_SAVED, { kind: kind || 'note' });

// Fire a global "a real milestone just changed" signal so long-lived widgets
// (e.g. the Getting Started checklist, which mounts once in AppLayout and would
// otherwise show a stale snapshot until a full reload) can re-fetch. Emitted
// from the API layer on successful milestone mutations — no per-page wiring.
import { META_PROFILE_FIELDS } from '../utils/profileMeta';

// Defined in its own module (see progressEvent.js) so listeners need not
// import this file; re-exported here because callers already expect it here.
import { PROGRESS_EVENT, notifyProgress } from './progressEvent';
import { invalidateBuilderStatus } from './builderStatusEpoch';
import { heldMomentId } from './signupMoments';
import { calendarKey, heldView, rememberView } from './deskViewMemory';

export { PROGRESS_EVENT };

// Get API base URLs from environment or use defaults
const VERTEXAI_KNOWLEDGE_BASE_URL = import.meta.env.VITE_KNOWLEDGE_BASE_VERTEXAI_URL;
const VERTEXAI_PROFILE_MANAGER_URL = import.meta.env.VITE_PROFILE_MANAGER_VERTEXAI_URL;
const KNOWLEDGE_BASE_URL = import.meta.env.VITE_KNOWLEDGE_BASE_URL;
const KNOWLEDGE_BASE_ES_URL = import.meta.env.VITE_KNOWLEDGE_BASE_ES_URL;
const KNOWLEDGE_BASE_UNIVERSITIES_URL = import.meta.env.VITE_KNOWLEDGE_BASE_UNIVERSITIES_URL;
const PROFILE_MANAGER_URL = import.meta.env.VITE_PROFILE_MANAGER_URL;
const PROFILE_MANAGER_ES_URL = import.meta.env.VITE_PROFILE_MANAGER_ES_URL;
const COUNSELOR_AGENT_URL = import.meta.env.VITE_COUNSELOR_AGENT_URL || 'https://us-east1-college-counselling-478115.cloudfunctions.net/counselor-agent';

// Which knowledge-base backend the app talks to. The ES and RAG AGENT
// services were retired 2026-09-16 (#2259); this still routes the KB reads.
const KNOWLEDGE_BASE_APPROACH = import.meta.env.VITE_KNOWLEDGE_BASE_APPROACH || 'rag';



// Determine profile manager URL based on approach
// Note: Hybrid and Elasticsearch approaches now use profile_manager_v2 (Firestore)
const getProfileManagerUrl = () => {
  // ALWAYS use Profile Manager V2 (Firestore) as per user instruction and V2 migration.
  // This ensures credits (which are managed by V2) work for all approaches.
  return import.meta.env.VITE_PROFILE_MANAGER_V2_URL || 'https://profile-manager-v2-pfnwjfp26a-ue.a.run.app';
};

// Create axios instance for agent API
const api = axios.create({
  timeout: 300000, // 5 minutes for agent processing (complex analysis with multiple sub-agents)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Note: We don't create static axios instances for profile/KB managers
// because the baseURL needs to change dynamically when user switches approaches.
// Instead, we'll use axios directly with the dynamic URL in each function.

// --- Verified caller identity (#223) ---------------------------------------
// The per-user backends (profile-manager-v2, counselor-agent) verify callers
// now: every request to them carries the signed-in user's Firebase ID token.
// The backend derives identity from the VERIFIED token — the X-User-Email
// header stays only as the legacy claimed-identity field it checks against.
// KB reads stay public (no PII) and get no token. Registered on BOTH the
// default axios object and the created `api` instance (instances do not
// inherit global interceptors).
// Max time to wait for Firebase to settle the initial auth state on a cold
// load before giving up and sending the request without a token. Real init is
// sub-second; this is only a safety valve so a pathological hang never freezes
// every authed request.
const AUTH_READY_TIMEOUT_MS = 5000;

// True when `url` targets a per-user backend (profile-manager-v2 / counselor)
// that must carry the Firebase token. Exact origin+path-prefix match — a bare
// startsWith would also match a look-alike host like `<base>.evil.com` and leak
// the token (#301 review). KB and any other URL return false (no token).
const PAYMENT_MANAGER_V2_URL = import.meta.env.VITE_PAYMENT_V2_API_URL
  || import.meta.env.VITE_PAYMENT_API_URL
  || 'https://payment-manager-v2-pfnwjfp26a-ue.a.run.app';
const KB_UNIVERSITIES_V2_URL = import.meta.env.VITE_KNOWLEDGE_BASE_UNIVERSITIES_URL
  || 'https://knowledge-base-manager-universities-v2-pfnwjfp26a-ue.a.run.app';
const MARKETING_AGENT_URL = import.meta.env.VITE_MARKETING_AGENT_URL
  || 'https://marketing-agent-pfnwjfp26a-ue.a.run.app';
const WORKFLOW_JOBS_URL = import.meta.env.VITE_WORKFLOW_JOBS_URL
  || 'https://workflow-jobs-pfnwjfp26a-ue.a.run.app';

const isAuthedBackendUrl = (url) => {
  // payment_manager_v2 verifies a Firebase token on every per-user endpoint,
  // KB's `chat` action is auth-gated (paid Gemini), and marketing_agent +
  // workflow_jobs are admin-only — so their calls must carry the token. KB
  // catalog reads are public and simply ignore the token.
  const authedBases = [getProfileManagerUrl(), COUNSELOR_AGENT_URL, PAYMENT_MANAGER_V2_URL, KB_UNIVERSITIES_V2_URL, MARKETING_AGENT_URL, WORKFLOW_JOBS_URL].filter(Boolean);
  return authedBases.some(
    (base) => url === base || url.startsWith(base + '/') || url.startsWith(base + '?'));
};

// Resolve the signed-in Firebase user for a request to an authed backend,
// waiting out the cold-load race first. Returns null for non-authed URLs or a
// signed-out user. Shared by the axios interceptor AND authedFetch so both the
// axios and fetch call paths attach the token identically.
const resolveAuthedUser = async (url, getAuthInstance) => {
  if (!isAuthedBackendUrl(url)) return null;
  const authInstance = getAuthInstance?.();
  let user = authInstance?.currentUser;
  // Cold-load race (#318): on first paint Firebase hasn't restored the
  // persisted session yet, so currentUser is null even for a signed-in user.
  // Requests firing in that window would go out tokenless and 401 under
  // AUTH_MODE=enforce (blank app). Wait for the initial auth state to settle
  // before concluding there's no user. authStateReady() resolves once (instant
  // thereafter); race a timeout so a hang can never freeze requests.
  if (!user && typeof authInstance?.authStateReady === 'function') {
    let timer;
    await Promise.race([
      authInstance.authStateReady().catch(() => {}),
      new Promise((resolve) => { timer = setTimeout(resolve, AUTH_READY_TIMEOUT_MS); }),
    ]);
    clearTimeout(timer);
    user = authInstance.currentUser;
  }
  return user || null;
};

export const attachAuthTokenInterceptor = (axiosLike, getAuthInstance) => {
  // Defensive: test environments mock axios without an interceptors API.
  if (!axiosLike?.interceptors?.request?.use) return;
  axiosLike.interceptors.request.use(async (config) => {
    try {
      const user = await resolveAuthedUser(config.url || '', getAuthInstance);
      if (user) {
        // getIdToken caches and auto-refreshes near expiry — cheap per call.
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${await user.getIdToken()}`;
      }
    } catch (e) {
      // Never block a request on token plumbing — the backend's AUTH_MODE
      // decides what a missing credential means.
      console.warn('[API] could not attach auth token:', e?.message);
    }
    return config;
  });
};

// fetch() bypasses axios interceptors entirely, so raw fetch() calls to the
// per-user backends (chat widgets, launchpad, deadlines) went out tokenless and
// 401'd under enforce. Use this wherever you'd fetch() an authed backend: it
// attaches the Firebase token (same auth-init-aware resolution as the axios
// interceptor) and otherwise behaves exactly like fetch. Non-authed URLs (KB,
// third parties) are passed straight through with no token.
export const authedFetch = async (url, options = {}) => {
  let headers = options.headers || {};
  try {
    const user = await resolveAuthedUser(url || '', () => firebaseAuth);
    if (user) {
      headers = { ...headers, Authorization: `Bearer ${await user.getIdToken()}` };
    }
  } catch (e) {
    console.warn('[API] could not attach auth token to fetch:', e?.message);
  }
  return fetch(url, { ...options, headers });
};

attachAuthTokenInterceptor(axios, () => firebaseAuth);
attachAuthTokenInterceptor(api, () => firebaseAuth);

// ── Marketing console (admin-only) ─────────────────────────────────────────
// All calls hit the admin-gated marketing_agent backend via authedFetch (the
// Firebase token is attached; the backend re-checks the email is on the
// marketing-admin allowlist). The signed-in email rides X-User-Email so the
// identity gate has a claimed identity to verify the token against.
const _mktHeaders = () => {
  const email = firebaseAuth?.currentUser?.email;
  return { 'Content-Type': 'application/json', ...(email ? { 'X-User-Email': email } : {}) };
};
const _mktJson = async (res) => {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    // The status rides the error: a caller tells "not deployed yet" (404)
    // and "already running" (409) apart from a real failure.
    const err = new Error(body.error || `Marketing request failed (${res.status})`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
};

// A stored Desk view (#5763): asked with the version the page holds; the
// server answers `unchanged` while none of the view's inputs moved, and the
// held body is the answer. The answer is kept for the next open
// (deskViewMemory).
const _deskView = async (path) => {
  const held = heldView(path);
  const have = typeof held?.view_version === 'string' ? held.view_version : '';
  const sep = path.includes('?') ? '&' : '?';
  const body = await _mktJson(await authedFetch(
    `${MARKETING_AGENT_URL}/${path}${have ? `${sep}have=${encodeURIComponent(have)}` : ''}`, { headers: _mktHeaders() }));
  if (body?.unchanged === true) {
    // Not held afresh: the body's clock-derived words age from when it
    // first came, so the two hour-long limits never add up (review).
    if (held && body.view_version === have) return held;
    // Never an empty answer: the held body went, so read it whole.
    const whole = await _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/${path}`, { headers: _mktHeaders() }));
    rememberView(path, whole);
    return whole;
  }
  rememberView(path, body);
  return body;
};

// A Desk read with no stored version: read whole, kept so a re-open shows
// the last answer while this one is out (deskViewMemory).
const _deskRead = async (path) => {
  const body = await _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/${path}`, { headers: _mktHeaders() }));
  rememberView(path, body);
  return body;
};

// The admin portal's sign-in check (ADR 0069, #5830): the server's admin
// list, asked with the signed-in account's verified token. Throws like every
// Desk call (a non-admin gets the 404 every admin route gives); the portal
// treats anything but a yes for its own account as no.
export const checkAdminAccess = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/admin-check`, { headers: _mktHeaders() }));

export const fetchMarketingDashboard = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/dashboard`, { headers: _mktHeaders() }));

export const listMarketingAssets = async ({ channel, status } = {}) => {
  const qs = new URLSearchParams();
  if (channel) qs.set('channel', channel);
  if (status) qs.set('status', status);
  const q = qs.toString() ? `?${qs}` : '';
  return _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/list${q}`, { headers: _mktHeaders() }));
};

export const updateMarketingAsset = async (id, fields) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/update`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ id, ...fields }),
  }));

export const setMarketingStatus = async (id, status, scheduled_for) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/status`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ id, status, scheduled_for }),
  }));

export const deleteMarketingAsset = async (id) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/delete`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ id }),
  }));

// ── Counselor/school outreach book (admin-only; strategy 2026-08-26) ───────
export const listOutreachContacts = async ({ segment, metro, status } = {}) => {
  const qs = new URLSearchParams();
  if (segment) qs.set('segment', segment);
  if (metro) qs.set('metro', metro);
  if (status) qs.set('status', status);
  const q = qs.toString() ? `?${qs}` : '';
  return _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/outreach-contacts${q}`, { headers: _mktHeaders() }));
};

export const createOutreachContact = async (contact) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/outreach-contacts`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify(contact || {}),
  }));

export const updateOutreachContact = async (id, fields) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/outreach-update`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ id, ...fields }),
  }));

export const logOutreachTouch = async (id, touch) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/outreach-touch`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ id, ...touch }),
  }));

export const fetchOutreachSummary = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/outreach-summary`, { headers: _mktHeaders() }));

export const saveOutreachGoals = async (goals) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/outreach-goals`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify(goals || {}),
  }));

// Live customer roster (#1207 C1): built at request time from Firestore —
// a customer who paid minutes ago appears on next load. Returns { roster:
// { users, count, as_of, snapshot_ts, snapshot_fields } }; segment/urgency
// scoring rides in from the daily snapshot, labeled by snapshot_ts.
export const fetchCustomers = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/customers`, { headers: _mktHeaders() }));

// ── Action Desk (#2408, ADR 0018): one card per thing that needs the founder.
// The click IS the approval: desk-act runs (or schedules) the card's action
// server-side and returns the updated card with its read-back proof. The
// server keys every action by (card, action), so a double click never acts
// twice. Contract: docs/design/2026-09-29-action-desk.md.
//
// A busy service (#4717): 2026-10-18 21:02Z long refreshes held every
// marketing-agent instance and this read got Cloud Run's 429, which carries
// no CORS headers, so the browser saw only "Failed to fetch". A network
// failure, a 429 or a 503 is retried once after DESK_BUSY_RETRY_MS; a second
// one throws an Error with `busy: true`, which the Desk words as busy (and
// retries on its own), never as an empty desk.
export const DESK_BUSY_RETRY_MS = 2000;
const _busyStatus = (status) => status === 429 || status === 503;
const _deskBusyError = (detail) => Object.assign(new Error(detail || 'the server is busy'), { busy: true });
export const getDeskCards = async ({ retryMs = DESK_BUSY_RETRY_MS } = {}) => {
  for (let attempt = 0; ; attempt += 1) {
    let res = null;
    let failure = null;
    try {
      res = await authedFetch(`${MARKETING_AGENT_URL}/desk-cards?view=today`, { headers: _mktHeaders() });
    } catch (e) {
      failure = e;
    }
    if (res && !_busyStatus(res.status)) return _mktJson(res);
    if (attempt >= 1) throw _deskBusyError(failure?.message || (res && `busy, ${res.status}`));
    await new Promise((resolve) => { setTimeout(resolve, retryMs); });
  }
};

// `version` is the card version the page showed (#2571): the server refuses
// the click when a steer changed the card since.
export const deskAct = async (cardId, actionId, args, version) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-act`, {
    method: 'POST', headers: _mktHeaders(),
    body: JSON.stringify({ card_id: cardId, action_id: actionId, ...(args ? { args } : {}),
      ...(Number.isInteger(version) ? { version } : {}) }),
  }));

export const deskCancel = async (cardId) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-cancel`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ card_id: cardId }),
  }));

// The live Desk's fast poll (#3114): read ONE running card back now (a Reel
// Facebook was still processing) instead of at the next 15-minute tick.
// The server takes one per card every 10 s and answers 429 inside that.
export const deskAdvance = async (cardId) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-advance`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ card_id: cardId }),
  }));

// Change time (#3196): move a post card, and every channel of its post, to
// `at` (an ISO instant). Answers {success, moved: {part: result}, card}; a
// refusal (a time passed, posting begun) throws with the server's words.
export const deskReschedule = async (cardId, at) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-reschedule`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ card_id: cardId, at }),
  }));

// The founder passes ONE held proofread claim after checking its source
// (#3339). `fp` binds it to the words that proofread read; a refusal (words
// changed, a claim that already stands) throws with the server's words.
export const deskProofreadOverride = async (cardId, claim, fp) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-proofread-override`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ card_id: cardId, claim, fp }),
  }));

export const deskDismiss = async (cardId, reason) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-dismiss`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ card_id: cardId, reason }),
  }));

// Batch approve (ADR 0018, "A with keys"): the primary action of up to ten
// cards at once. The server re-checks every card and refuses the whole batch
// when one cannot run: that answer (409, {success:false, refused:[{card_id,
// title, reason}]}) is RETURNED, not thrown, so the page can say which card
// and why. Success: {success:true, cards:[...]} with each card's proof.
export const deskActBatch = async (cardIds, versions) => {
  const res = await authedFetch(`${MARKETING_AGENT_URL}/desk-act-batch`, {
    method: 'POST', headers: _mktHeaders(),
    body: JSON.stringify({ card_ids: cardIds, ...(versions ? { versions } : {}) }),
  });
  if (res.status === 409) {
    const body = await res.json().catch(() => ({}));
    return { success: false, error: body.error || 'nothing ran', refused: Array.isArray(body.refused) ? body.refused : [] };
  }
  return _mktJson(res);
};

// The marketing calendar and the numbers on post cards (#3152): every post
// in a Pacific window (start YYYY-MM-DD, at most 42 days), one entry per
// piece of work, with each channel's live link and the daily read's numbers.
export const getDeskCalendar = async (start, days) => _deskView(calendarKey(start, days));

// Friends and family outreach (#3266): one card's group strip and follower
// table, and the founder's Sent ticks, Copy clicks and typed WhatsApp count.
// Only group labels and channel ticks travel: no field takes a person.
export const getDeskOutreach = async (cardId) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-outreach?card_id=${encodeURIComponent(cardId)}`,
    { headers: _mktHeaders() }));

export const deskOutreach = async (cardId, op, { channel, value } = {}) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-outreach`, {
    method: 'POST', headers: _mktHeaders(),
    body: JSON.stringify({ card_id: cardId, op, ...(channel ? { channel } : {}), ...(value !== undefined ? { value } : {}) }),
  }));

// The send list on a friends and family card (#3408): op is tick, untick,
// skip, unskip, add ({name, type}), remove, or move ({to_card_id}); every op
// but add names its item_id. Answers the card's desk-outreach view.
export const deskOutreachSendList = async (cardId, op, { itemId, name, type, toCardId } = {}) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-outreach-send-list`, {
    method: 'POST', headers: _mktHeaders(),
    body: JSON.stringify({
      card_id: cardId, op, ...(itemId ? { item_id: itemId } : {}), ...(name !== undefined ? { name } : {}),
      ...(type ? { type } : {}), ...(toCardId ? { to_card_id: toCardId } : {}),
    }),
  }));

// The agent's kill switch (charter rule 7): one switch holds every rule and
// every scheduled action (app_config/desk.paused). Returns {paused}.
export const deskSetPaused = async (paused) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-pause`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ paused: Boolean(paused) }),
  }));

// Tell the Desk (#5155): his requests. 404 while the desk_requests flag is
// off for him. getDeskRequests -> {requests, counts}; sendDeskRequest ->
// {request} (R-n, new); deskRequestAct(id, op, text) with op reply |
// thats_all | withdraw | run_again -> {request} (a pick: deskRequestPick); a request that moved since
// the page read it answers 409 in words.
export const getDeskRequests = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-requests`, { headers: _mktHeaders() }));

export const sendDeskRequest = async (text, images = []) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-request`, {
    method: 'POST', headers: _mktHeaders(),
    body: JSON.stringify({ text, ...(images.length ? { images } : {}) }),
  }));

export const deskRequestAct = async (id, op, text, images = []) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-request-act`, {
    method: 'POST', headers: _mktHeaders(),
    body: JSON.stringify({ id, op, ...(text ? { text } : {}), ...(images.length ? { images } : {}) }),
  }));

// His Pick on mocks offered on a request's thread (#5185): the offer it
// answers and the letter, or "none" -> {request}; a pick on mocks that were
// replaced or already picked answers 409.
export const deskRequestPick = async (id, offer, choice) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-request-act`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ id, op: 'pick', offer, choice }),
  }));

// Tell the Desk's images (#5178, founder pick A). The body is the file's own
// bytes (the server reads its type from them, 10 MB at most) -> {image: {id,
// type, bytes, name}}, staged for the next send. The read answers the bytes
// of an image on that request only (never a public or signed URL), as a Blob
// the page shows through an object URL.
export const uploadDeskRequestImage = async (file) => {
  // The name rides a header, never the URL (request logs keep URLs).
  const headers = {
    ..._mktHeaders(), 'Content-Type': file.type || 'application/octet-stream',
    'X-Desk-Image-Name': encodeURIComponent(String(file.name || '').slice(0, 200)),
  };
  return _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-request-image`, {
    method: 'POST', headers, body: file,
  }));
};

export const getDeskRequestImage = async (requestId, imageId) => {
  const q = `request=${encodeURIComponent(requestId)}&id=${encodeURIComponent(imageId)}`;
  const res = await authedFetch(`${MARKETING_AGENT_URL}/desk-request-image?${q}`, { headers: _mktHeaders() });
  if (!res.ok) {
    const err = new Error(`image ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.blob();
};

// Request a change on any card (#5344; Steer, #2571): the founder's note on
// one card, stored and queued for the runner (the card locked while the desk
// works; the answer arrives with a later desk-cards), or handed to Claude as
// a request at once (the card's thread says "Sent to Claude: R-n"). `images`:
// Tell the Desk's staged upload ids, sent only when there are any.
export const deskSteer = async (cardId, text, images = []) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-steer`, {
    method: 'POST', headers: _mktHeaders(),
    body: JSON.stringify({ card_id: cardId, text, ...(images && images.length ? { images } : {}) }),
  }));

// The founder's answer to one open question on a feature proposal's Spec kit
// (#5027, pick A): the question's text as the card showed it, and his words.
// Returns {card} with the answer on spec_kit.answers; a question that changed
// since the page loaded is refused in words (409).
export const answerSpecQuestion = async (cardId, question, text) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-spec-answer`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ card_id: cardId, question, text }),
  }));

// "Accept the Desk's answers" on a proposal's Spec kit (#5121): the Desk's
// recommended answers exactly as the card showed them ([{q, text}], each one
// he left untouched), recorded as his answers in one write. Returns {card,
// accepted}; a question or answer that changed since the page loaded is
// refused in words (409), nothing written.
export const acceptSpecDefaults = async (cardId, accept) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-spec-accept-defaults`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ card_id: cardId, accept }),
  }));

// The launch kit's boxes (#5129, pick A) on a feature proposal or go-live
// card: {short, email, tutorial: true|false}, 1 to 3 of them, stored with who
// and when. Returns {card}; a kit the card cannot launch is refused in words (409).
export const setLaunchPieces = async (cardId, pieces) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-launch-pieces`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ card_id: cardId, pieces }),
  }));

// The ranker's picks (#5156, pick A): his Swap / Hold / Pin on a ranked Short
// place ({op, candidate, date, time, reason}) or Add to the plan / Hold on a
// Next tutorials pick ({op, candidate, reason}). Returns {op, card}; a pick the
// card did not offer is refused in words (409); off is a 404.
export const rankOverride = async (cardId, body) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-rank-override`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ card_id: cardId, ...body }),
  }));

// Growth > Next up (#5156): every candidate the ranker scored, its score's
// parts and readings. A 404 while desk_ranker is off.
export const getDeskNextUp = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-next-up`, { headers: _mktHeaders() }));

// The repo copy of a proposal's mock page (#5092): the page committed beside
// its specs, read from main by the server for the founder, so the mocks open
// whichever claude.ai account owns the artifact link. Returns {html, source, url}.
export const fetchSpecMocks = async (cardId) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-spec-mocks`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ card_id: cardId }),
  }));

// Re-render from the card (#3533, founder pick A1): an optional note; the
// server cancels a schedule first and queues the Mac's video_rerender job,
// or refuses in words (inside 90 minutes of the slot). {cancel: true}
// withdraws a request still in flight. Returns {card, unchanged}.
export const deskRerender = async (cardId, note, { cancel = false } = {}) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-rerender`, {
    method: 'POST', headers: _mktHeaders(),
    body: JSON.stringify(cancel ? { card_id: cardId, cancel: true } : { card_id: cardId, ...(note ? { note } : {}) }),
  }));

// Have Claude do it (#3656, founder pick A1): op ask (an optional note;
// "Change it" asks again with one), send (his click, bound to the sha of the
// words he saw) or cancel. Returns {card, unchanged}; refusals in words.
export const deskClaudeTask = async (cardId, op, { note, sha } = {}) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-claude-task`, {
    method: 'POST', headers: _mktHeaders(),
    body: JSON.stringify({ card_id: cardId, op, ...(note ? { note } : {}), ...(sha ? { sha } : {}) }),
  }));

// "Remember this?" on a steer's answer: op remember (text = edited wording,
// optional) | once. Returns {card, learnings}.
export const deskSteerLearn = async (cardId, messageId, op, text) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-steer-learn`, {
    method: 'POST', headers: _mktHeaders(),
    body: JSON.stringify({ card_id: cardId, message_id: messageId, op, ...(text ? { text } : {}) }),
  }));

// What the desk learned from the founder: op edit (with text) | pause |
// resume | delete. Returns {learning, learnings}.
export const deskLearningOp = async (id, op, text) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-learnings`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ id, op, ...(text ? { text } : {}) }),
  }));

// The relevance gate (#2637): the Card filter setting (off | shadow | on),
// and showing a card the desk hid (an optional note becomes a learning).
export const deskSetCardFilter = async (mode) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-card-filter`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ mode }),
  }));
export const deskRestoreCard = async (cardId, text) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-relevance-restore`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ card_id: cardId, ...(text ? { text } : {}) }),
  }));

// A rule the desk follows on its own: op pause | resume | delete. Returns
// {rule} (or {rule: {deleted}} on delete).
export const deskRuleOp = async (ruleId, op) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-rules`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ rule_id: ruleId, op }),
  }));

// The desk's Operations view (the old Engineering tab, ADR 0018 addendum
// 2026-09-29): services health, cost, tests and Astra in the field, read
// from desk_signals/ops as the runner last collected them. Returns
// {updated_at, sections: {name: {state, status, source, as_of, error, data}}}.
export const getDeskOps = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-ops`, { headers: _mktHeaders() }));

// The Growth, Search view (#2565): desk_signals/seo as the runner last
// collected it. {updated_at, sections: {name: {state, ...}}, drafts}.
export const getDeskSeo = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-seo`, { headers: _mktHeaders() }));

// The founder's "I requested indexing" on a re-index row (#3657): records
// that he asked Search Console himself. {path, requested_at, requested_by}.
export const markDeskSeoReindexRequested = async (path) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-seo-reindex-requested`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ path }),
  }));

// The executive summary on Growth and Search & SEO (#3470, pick A):
// desk_briefs/{tab} as the runner's daily read wrote it. {tab, state:
// ready|drafting|unavailable|missing, stale, stamp, tiles, prose, ...}.
export const getDeskBrief = async (tab) => _deskRead(`desk-brief?tab=${encodeURIComponent(tab)}`);

// The KB accuracy view (#3057): kb_validation/_summary as the runner last
// wrote it. {state, source, labels, schools, clean, with_errors,
// needs_reread, issues, errors, by_check, worst, reread, sections, ...}.
export const getDeskKb = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-kb`, { headers: _mktHeaders() }));

// The KB fact ledger (#4226, ADR 0050): the fleet summary and the newest
// changes; `school` for one school's rows by family; `row` for one row's
// history; `job` for a picked re-check's status and result (#4924).
// {state, rows, by_status, by_family, per_school, feed, ...}.
export const getDeskKbLedger = async ({ school = null, row = null, job = null } = {}) => {
  const q = school ? `?school=${encodeURIComponent(school)}` : row ? `?row=${encodeURIComponent(row)}`
    : job ? `?job=${encodeURIComponent(job)}` : '';
  return _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-kb-ledger${q}`, { headers: _mktHeaders() }));
};

// The KB ledger's two actions on picked sections and figures (#4924):
// {action: 'recheck'|'source', picks: [{school, section, anyway}], rows: [ids]}.
// recheck -> {success, job_id, picks, rows}; source -> {success, schools, waiting}.
export const postDeskKbLedgerAct = async (body) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-kb-ledger-act`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify(body),
  }));

// Growth > Sessions (#4477): every real visitor session's record, the
// returning visitors' journeys and the day's digest. `session` for one
// record (with its replay link), `person` for one visitor's journey; the
// filters (source, device, outcome, tag) narrow the recent list.
export const getDeskSessions = async (args = {}) => {
  const keys = ['session', 'person', 'source', 'device', 'outcome', 'tag'];
  const q = keys.filter((k) => args[k]).map((k) => `${k}=${encodeURIComponent(args[k])}`).join('&');
  // The tab's own open is a stored view (#5763); a record, a journey or a
  // filter is read fresh.
  if (!q) return _deskView('desk-sessions');
  return _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-sessions?${q}`, { headers: _mktHeaders() }));
};

// The Growth, Audience view (#2600, #2621): who asks Astra Lite, from the
// daily aggregates, cells under 5 hidden by the server.
// {as_of, min_cell, since, source, windows: {7d, 28d}, trend}.
export const getDeskAudience = async () => _deskRead('desk-audience');

// The Comments tab (#3804, ADR 0041, founder pick A1): every comment kept,
// its thread, draft, proofread and what he may do; the counts and the
// measurement strip. {comments, counts, by_platform, needs_you, flag_open,
// posts, measure, youtube_send, limits, read, linkedin}.
export const getDeskComments = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-comments`, { headers: _mktHeaders() }));

// His Send / Skip / Hide / Delete / Edit reply / Delete reply / I've seen it /
// Leave it on ONE comment. The page holds a Send 10 seconds with Undo first.
export const deskCommentAct = async (comment, op, extra) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-comment-act`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ comment, op, ...(extra || {}) }),
  }));

// The house rules on his words as he types (the server is the one definition).
export const deskCommentCheck = async (comment, body, words) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-comment-check`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ comment, body, words }),
  }));

// The TikTok tab (#4649): every live Short not yet on TikTok, newest first,
// with its file name and TikTok words. {state, items, per_day, posted_today,
// hint, disclosure, unexplained, coming, handle}.
export const getDeskTiktok = async () => _deskView('desk-tiktok');

// His I posted it on one Short, the TikTok link optional. {slug, summary, link}.
export const deskTiktokPosted = async (slug, url) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-tiktok-posted`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ slug, ...(url ? { url } : {}) }),
  }));

// His Get it ready on TikTok on one Short while drafts are off (#5510): the
// runner stages it in TikTok Studio and stops before Post. {slug, status, line}.
export const deskTiktokPrepare = async (slug) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-tiktok-prepare`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ slug }),
  }));

// Growth's Ads view (#4088): the Deadline-Season Ads Plan's spend, credit,
// funnel and rules. {state, plan, credit, channels, total, groups, schools, days, reads}.
export const getDeskAds = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-ads`, { headers: _mktHeaders() }));

// Growth's YouTube block (#3544): YouTube Studio's own views by source, the
// search terms, feed vs search by day and each Short's retention, with the
// data floor. {state, sources, terms, days, shorts, floor, through, ...}.
export const getDeskYoutube = async () => _deskRead('desk-youtube');

// Growth > Earnings (#5117): what each channel's views earn through Stratia,
// all time (views, visits, sign-ups, paid passes; ads apart), and the
// monetization watch. {state, channels, totals, stripe, watch, programs, ...}.
export const getDeskMonetization = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-monetization`, { headers: _mktHeaders() }));

// Growth's reach map (#4742): every US state and country any source reached,
// with each source's count per place. Admin-only, never the public page.
// {us, world, totals, sources, reach, updated_at}.
export const getDeskReach = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-reach`, { headers: _mktHeaders() }));

// Growth's Posts section (#3405, pick B): every post of the last 28 days by
// channel, the channel strip and the Desk's fixed-rule insights.
// {read_at, window_days, posts, channels, totals, insights}.
export const getDeskPosts = async () => _deskView('desk-posts');

// One insight onto Today as a card; the server recomputes it. {card_id, title, event}.
export const deskPostsAct = async (insight) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-posts-act`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ insight }) }));

// Names the Desk post an unmatched read belongs to, by its campaign tag.
export const deskPostsAttach = async ({ channel, id, campaign }) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-posts-attach`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ channel, id, campaign }) }));

// The same aggregates, every week, as CSV text (admin only). {csv, rows, note}.
export const getDeskAudienceCsv = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-audience-csv`, { headers: _mktHeaders() }));

// The Agent activity view (ADR 0020): the supervisor's recent runs (plan,
// note, spans with model, status, time, cost and cards) and, for one card,
// where it came from. {runs, specialists} or {provenance}.
export const getDeskAgentActivity = async () =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-agent-activity`, { headers: _mktHeaders() }));
export const getDeskCardProvenance = async (cardId) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-agent-activity?card_id=${encodeURIComponent(cardId)}`,
    { headers: _mktHeaders() }));

export const deskRefresh = async ({ dryRun = false } = {}) =>
  _mktJson(await authedFetch(`${MARKETING_AGENT_URL}/desk-refresh`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify(dryRun ? { dry_run: true } : {}),
  }));

// ── Workflow jobs (admin-only queue for Claude workflows) ──────────────────
// The console enqueues a job; a Claude runner (agents/workflow_runner) claims
// it, runs the real Workflow script, and writes status/result back. Same admin
// token plumbing as the marketing calls (authedFetch + X-User-Email).
export const enqueueWorkflowJob = async (type, args) =>
  _mktJson(await authedFetch(`${WORKFLOW_JOBS_URL}/enqueue`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ type, args }),
  }));

export const listWorkflowJobs = async ({ status, type, limit } = {}) => {
  const qs = new URLSearchParams();
  if (status) qs.set('status', status);
  if (type) qs.set('type', type);
  if (limit) qs.set('limit', String(limit));
  const q = qs.toString() ? `?${qs}` : '';
  return _mktJson(await authedFetch(`${WORKFLOW_JOBS_URL}/list${q}`, { headers: _mktHeaders() }));
};

// Runner liveness (founder ask 2026-07-30): every registered background
// worker merged with whether it has heartbeated recently, and — when it
// hasn't — the exact command to (re)start it.
export const listRunnerStatus = async () =>
  _mktJson(await authedFetch(`${WORKFLOW_JOBS_URL}/runners`, { headers: _mktHeaders() }));

export const getWorkflowJob = async (id) =>
  _mktJson(await authedFetch(`${WORKFLOW_JOBS_URL}/get?id=${encodeURIComponent(id)}`, { headers: _mktHeaders() }));

export const cancelWorkflowJob = async (id) =>
  _mktJson(await authedFetch(`${WORKFLOW_JOBS_URL}/cancel`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ id }),
  }));

console.log(`[FRONTEND] Using knowledge base approach: ${KNOWLEDGE_BASE_APPROACH}`);
console.log(`[FRONTEND] Profile manager URL: ${getProfileManagerUrl()}`);

// Session management - use localStorage to persist across page changes
const SESSION_STORAGE_KEY = 'college_counselor_session_id';
const APPROACH_STORAGE_KEY = 'knowledgeBaseApproach';

const getSessionId = () => {
  return localStorage.getItem(SESSION_STORAGE_KEY);
};

const setSessionId = (sessionId) => {
  localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
};

const clearSessionId = () => {
  localStorage.removeItem(SESSION_STORAGE_KEY);
};





/**
 * Get session history
 */
export const getSession = async (sessionId) => {
  try {
    const response = await api.get(`/apps/agents/users/user/sessions/${sessionId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting session:', error);
    throw error;
  }
};

/**
 * Upload student profile document to the user-specific student_profile store
 * Uses the profile manager cloud function
 */
export const uploadStudentProfile = async (file, userEmail) => {
  try {
    if (!userEmail) {
      throw new Error('User email is required for profile upload');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_email', userEmail);  // Use user_email as backend expects

    // Call the profile manager cloud function with dynamic URL
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/upload-profile`, formData, {
      timeout: 180000,
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-User-Email': userEmail
      },
    });
    // The Getting Started checklist observes this event — without it a
    // student who just told us about themselves watched 'Tell us about
    // you' sit unchecked until a full page reload.
    if (response.data?.success !== false) { notifyProgress(); warmFitsPrecompute(userEmail); }
    return response.data;
  } catch (error) {
    console.error('Error uploading profile:', error);
    // Guardrail rejections (unsupported_type, file_too_large, ...) come back
    // as 400s with a human-readable `error` — surface that instead of axios's
    // opaque "Request failed with status code 400".
    const serverMessage = error.response?.data?.error;
    throw serverMessage ? new Error(serverMessage) : error;
  }
};

/**
 * List documents in user-specific student_profile store
 * Uses the profile manager cloud function
 */
export const listStudentProfiles = async (userEmail) => {
  try {
    if (!userEmail) {
      throw new Error('User email is required to list profiles');
    }

    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/list-profiles`, {
      timeout: 180000,
      params: { user_email: userEmail },  // Use user_email and /list-profiles endpoint
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error listing profiles:', error);
    throw error;
  }
};

/**
 * Delete a student profile document
 * Uses the profile manager cloud function
 */
export const deleteStudentProfile = async (documentId, userEmail, filename) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.delete(`${baseUrl}/delete-profile`, {
      timeout: 180000,
      data: {
        document_name: documentId,
        user_email: userEmail,
        filename: filename
      },
      headers: { 'X-User-Email': userEmail }
    });
    // The Getting Started checklist observes this event — without it a
    // student who just told us about themselves watched 'Tell us about
    // you' sit unchecked until a full page reload.
    if (response.data?.success !== false) notifyProgress();
    return response.data;
  } catch (error) {
    console.error('Error deleting profile:', error);
    throw error;
  }
};

/**
 * Reset all profile data - clears profile, fit analyses, and optionally college list
 * Use for complete profile reset / fresh start
 */
export const resetAllProfile = async (userEmail, deleteCollegeList = false) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/reset-all-profile`, {
      user_email: userEmail,
      delete_college_list: deleteCollegeList
    }, {
      timeout: 180000,
      headers: { 'X-User-Email': userEmail }
    });
    // The Getting Started checklist observes this event — without it a
    // student who just told us about themselves watched 'Tell us about
    // you' sit unchecked until a full page reload.
    if (response.data?.success !== false) notifyProgress();
    return response.data;
  } catch (error) {
    console.error('Error resetting profile:', error);
    throw error;
  }
};

/**
 * Get student profile document content for preview
 * Uses the profile manager cloud function
 */
export const getStudentProfileContent = async (userEmail, filename) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/get-profile-content`, {
      user_email: userEmail,
      filename: filename
    }, {
      timeout: 180000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting profile content:', error);
    throw error;
  }
};

/**
 * Fetch the complete user profile from Profile Manager ES
 * Uses /get-profile endpoint which returns both structured data and markdown content
 * @param {string} userEmail - User's email address
 * @returns {object} Profile data with success, content (markdown), and profile (structured) fields
 */
export const fetchUserProfile = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    console.log(`[API] Fetching profile for ${userEmail} from ${baseUrl}`);

    const response = await axios.get(`${baseUrl}/get-profile`, {
      params: { user_email: userEmail },
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });

    if (response.data?.success && (response.data?.profile || response.data?.content)) {
      const profile = response.data.profile || {};
      const content = response.data.content || response.data.markdown || '';

      console.log(`[API] Profile found, content length: ${content.length}, has structured: ${!!response.data.profile}`);

      return {
        success: true,
        content: content,  // Raw markdown content
        profile: profile,  // Structured data (personal_info, academics, etc.)
        metadata: response.data.metadata || {}
      };
    }

    console.log(`[API] No profile found for ${userEmail}`);
    return {
      success: false,
      error: 'No profile found',
      content: '',
      profile: null
    };
  } catch (error) {
    // The backend answers a missing profile with HTTP 404, which axios
    // throws — but a definitive 404 IS "no profile", and AppLayout's
    // /welcome redirect matches the exact string 'No profile found'.
    // Only a real 404 maps; transport errors (timeout, 5xx) keep their
    // message so a returning user is never mis-redirected into onboarding.
    if (error.response?.status === 404) {
      console.log(`[API] No profile found (404) for ${userEmail}`);
      return {
        success: false,
        error: 'No profile found',
        content: '',
        profile: null
      };
    }
    console.error('Error fetching user profile:', error);
    return {
      success: false,
      error: error.message,
      content: '',
      profile: null
    };
  }
};

/**
 * Fetch structured profile data (JSON) for visual display
 * Wrapper around fetchUserProfile that extracts just the structured data
 * @param {string} userEmail - User's email address
 * @returns {object} Structured profile data with fields like personal_info, academics, etc.
 */
export const fetchStructuredProfile = async (userEmail) => {
  const result = await fetchUserProfile(userEmail);

  if (result.success && result.profile) {
    console.log(`[API] Structured profile found`);
    return {
      success: true,
      profile: result.profile
    };
  }

  return {
    success: false,
    error: result.error || 'No profile found',
    profile: null
  };
};

/**
 * The canonical profile field schema, served by the backend
 * (/get-profile-schema — profile_schema.py's PROFILE_SCHEMA verbatim).
 * The backend has always served this to agents; the frontend consuming it
 * is redesign Phase 1/2: the "worth doing next" ordering comes from its
 * interview_order instead of a hand-maintained frontend copy. Returns
 * null on any failure — callers fall back to their local ordering rather
 * than rendering a broken surface.
 * @returns {object|null} { version, fields: [{name, type, ask, ...}], interview_order: [...] }
 */
export const getProfileSchema = async () => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/get-profile-schema`, {
      timeout: 10000,
    });
    return response.data?.success ? (response.data.schema || null) : null;
  } catch (error) {
    console.error('[API] get-profile-schema failed (falling back to local order):', error?.message);
    return null;
  }
};

/**
 * Update a specific field in the structured profile
 * Uses the /update-structured-field endpoint for safe field-level updates
 * @param {string} userEmail - User's email address
 * @param {string} fieldPath - The field to update (e.g., 'gpa_weighted', 'courses')
 * @param {any} value - The new value
 * @param {string} operation - 'set', 'append', or 'remove' (default: 'set')
 * @returns {object} Result with success status
 */
export const updateProfileField = async (userEmail, fieldPath, value, operation = 'set') => {
  try {
    const url = `${getProfileManagerUrl()}/update-structured-field`;
    console.log(`[API] Updating profile field: ${fieldPath} with operation: ${operation}`);

    const response = await axios.post(url, {
      user_email: userEmail,
      field_path: fieldPath,
      value: value,
      operation: operation
    });

    if (response.data.success) {
      notifyProgress();   // checklist observes profile writes
      // Warm the odds only for real profile content — meta fields like
      // visited_sections are written on every navigation.
      if (!META_PROFILE_FIELDS.has(String(fieldPath).split('.')[0])) {
        warmFitsPrecompute(userEmail);
      }
      console.log(`[API] Successfully updated ${fieldPath}`);
      return {
        success: true,
        message: response.data.message || 'Field updated successfully'
      };
    } else {
      console.error(`[API] Failed to update ${fieldPath}:`, response.data.error);
      return {
        success: false,
        error: response.data.error || 'Update failed'
      };
    }
  } catch (error) {
    // A "Network Error" (no response) is transient — usually the request was
    // canceled by a navigation (best-effort telemetry like visited_sections
    // fires on every route change) or a brief drop, not a server problem. Keep
    // those quiet; real server errors carry a response status and stay loud.
    if (error?.response) {
      console.error(`[API] Error updating profile field ${fieldPath}:`, error);
    } else {
      console.debug(`[API] profile field ${fieldPath} update skipped (network):`, error?.message);
    }
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * ADR 0009 Phase 3 — story wizard certificates screen: a photographed
 * certificate/badge/belt becomes one clean `certificates` array entry via
 * Gemini vision.
 */
export const extractCertificateImage = async (userEmail, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_email', userEmail);

    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/extract-certificate-image`, formData, {
      timeout: 60000,
      headers: { 'Content-Type': 'multipart/form-data', 'X-User-Email': userEmail },
    });
    if (response.data?.success) { notifyProgress(); warmFitsPrecompute(userEmail); }
    return response.data;
  } catch (error) {
    const serverMessage = error.response?.data?.error;
    return { success: false, error: serverMessage || error.message };
  }
};

/**
 * Warm the whole-KB deterministic fit-preview cache for this user (no LLM, no
 * credit). Best-effort + fire-and-forget: the backend skips the recompute when
 * the profile is unchanged, and the retrieval recomputes lazily if this misses.
 * Call after a profile is loaded/saved so list-building is instant later.
 */
// Bulk read of the student's per-school preview bands — the source for
// "your odds on every card". The backend computes first (self-skipping when
// the stored previews already match the profile), so this is instant when
// warm and a seconds-long deterministic pass when cold.
export const getFitPreviews = async (userEmail) => {
  if (!userEmail) return { success: false, previews: {} };
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/get-fit-previews`,
      { user_email: userEmail },
      { headers: { 'X-User-Email': userEmail }, timeout: 90000 });
    return response.data;
  } catch (error) {
    console.warn('[API] get fit previews failed:', error?.message);
    return { success: false, previews: {} };
  }
};

export const precomputeFitPreviews = async (userEmail) => {
  if (!userEmail) return;
  try {
    await axios.post(`${getProfileManagerUrl()}/precompute-fit-previews`,
      { user_email: userEmail },
      { headers: { 'X-User-Email': userEmail }, timeout: 60000 });
  } catch (error) {
    console.warn('[API] precompute fit previews failed (non-blocking):', error?.message);
  }
};

// Demand telemetry (founder ask 2026-07-30): what students actually search
// for — misses become the KB expansion queue. Fire-and-forget; a search UI
// must never wait on it.
export const logSchoolSearch = async (userEmail, query, found, source) => {
  if (!userEmail || !query || String(query).trim().length < 3) return;
  try {
    await axios.post(`${getProfileManagerUrl()}/log-school-search`,
      { user_email: userEmail, query: String(query).trim(), found: !!found, source },
      { headers: { 'X-User-Email': userEmail }, timeout: 10000 });
  } catch { /* telemetry never surfaces */ }
};

/**
 * Save onboarding profile data
 * Saves the collected onboarding data directly to the structured profile
 * @param {string} userEmail - User's email address
 * @param {object} onboardingData - Onboarding form data
 * @returns {object} Result with success status
 */
export const saveOnboardingProfile = async (userEmail, onboardingData) => {
  try {
    const baseUrl = getProfileManagerUrl();
    console.log(`[API] Saving onboarding profile for ${userEmail}`);

    // Save onboarding data as initial profile
    const response = await axios.post(`${baseUrl}/save-onboarding-profile`, {
      user_email: userEmail,
      profile_data: onboardingData
    }, {
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });

    console.log(`[API] Onboarding profile saved:`, response.data);
    // The Getting Started checklist observes this event — without it a
    // student who just told us about themselves watched 'Tell us about
    // you' sit unchecked until a full page reload.
    if (response.data?.success !== false) { notifyProgress(); warmFitsPrecompute(userEmail); }
    return response.data;
  } catch (error) {
    console.error('[API] Error saving onboarding profile:', error);
    return {
      success: false,
      error: error.message
    };
  }
};






/**
 * Save university chat conversation
 * @param {string} userEmail - User's email
 * @param {string} universityId - University ID
 * @param {string} universityName - University display name
 * @param {Array} messages - Conversation messages
 */
export const saveUniversityChat = async (userEmail, universityId, universityName, messages) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/university-chat-save`, {
      user_email: userEmail,
      university_id: universityId,
      university_name: universityName,
      messages: messages
    }, {
      timeout: 10000,
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error saving university chat:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Load university chat conversation
 * @param {string} userEmail - User's email
 * @param {string} universityId - University ID
 */
export const loadUniversityChat = async (userEmail, universityId) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/university-chat-load`, {
      user_email: userEmail,
      university_id: universityId
    }, {
      timeout: 10000,
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error loading university chat:', error);
    return { success: false, messages: [], error: error.message };
  }
};

/**
 * Extract text content and suggested questions from the agent's response
 * Returns an object with { text, suggested_questions }
 */
export const extractResponseText = (sessionData) => {
  if (!sessionData || !sessionData.events) {
    return '';
  }

  // Get the last event
  const lastEvent = sessionData.events[sessionData.events.length - 1];

  if (!lastEvent || !lastEvent.content || !lastEvent.content.parts) {
    return '';
  }

  // Extract text from all parts
  const rawText = lastEvent.content.parts
    .filter(part => part.text)
    .map(part => part.text)
    .join('\n\n');

  // Try to parse as JSON (OrchestratorOutput format)
  try {
    const parsed = JSON.parse(rawText);
    if (parsed.result !== undefined) {
      // Return the result text (for backward compatibility)
      return parsed.result;
    }
  } catch (e) {
    // Not JSON, return as-is
  }

  return rawText;
};


// ============================================
// Knowledge Base Management API
// ============================================

// Determine knowledge base URL based on approach
const getKnowledgeBaseUrl = () => {
  const approach = localStorage.getItem('knowledgeBaseApproach') || KNOWLEDGE_BASE_APPROACH;
  if (approach === 'hybrid') {
    // Hybrid uses the universities knowledge base
    return import.meta.env.VITE_KNOWLEDGE_BASE_UNIVERSITIES_URL || 'https://knowledge-base-manager-universities-pfnwjfp26a-ue.a.run.app';
  }
  if (approach === 'elasticsearch') {
    return import.meta.env.VITE_KNOWLEDGE_BASE_ES_URL || 'https://knowledge-base-manager-es-pfnwjfp26a-ue.a.run.app';
  }
  if (approach === 'vertexai') {
    return import.meta.env.VITE_KNOWLEDGE_BASE_VERTEXAI_URL || 'https://knowledge-base-manager-vertexai-pfnwjfp26a-ue.a.run.app';
  }
  return import.meta.env.VITE_KNOWLEDGE_BASE_URL || 'https://knowledge-base-manager-pfnwjfp26a-ue.a.run.app';
};

// Note: knowledgeBaseApi removed - we use dynamic URLs in each function call

console.log(`[FRONTEND] Knowledge base URL: ${getKnowledgeBaseUrl()}`);

/**
 * Upload university research document to the college_admissions_kb store
 * Uses the knowledge base manager cloud function
 * Works with all approaches (RAG, Firestore, Elasticsearch) using the same endpoint
 */
export const uploadKnowledgeBaseDocument = async (file, userId) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (userId) {
      formData.append('user_id', userId);
    }

    const baseUrl = getKnowledgeBaseUrl();
    const response = await axios.post(`${baseUrl}/upload-document`, formData, {
      timeout: 180000,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(userId && { 'X-User-Email': userId })
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading knowledge base document:', error);
    console.error('Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: getKnowledgeBaseUrl() + '/upload-document'
    });
    throw error;
  }
};

/**
 * List all documents in the knowledge base
 * Uses the knowledge base manager cloud function
 * Works with all approaches (RAG, Firestore, Elasticsearch) using the same endpoint
 * Note: Hybrid approach uses universities KB with different response format
 */
export const listKnowledgeBaseDocuments = async (userId) => {
  try {
    const approach = localStorage.getItem('knowledgeBaseApproach') || KNOWLEDGE_BASE_APPROACH;
    const baseUrl = getKnowledgeBaseUrl();

    // Hybrid approach uses universities knowledge base with different endpoint
    if (approach === 'hybrid') {
      // Universities KB uses root endpoint and returns 'universities' array
      const response = await axios.get(baseUrl, {
        timeout: 60000
      });

      // Transform universities response to document format for UI compatibility
      if (response.data.success && response.data.universities) {
        const documents = response.data.universities.map(uni => ({
          name: uni.university_id,
          display_name: uni.official_name,
          mime_type: 'university/profile',
          size_bytes: 0,
          size: 0,
          create_time: uni.indexed_at,
          state: 'ACTIVE',
          location: uni.location,
          acceptance_rate: uni.acceptance_rate,
          market_position: uni.market_position
        }));

        return {
          success: true,
          documents: documents,
          total: response.data.total
        };
      }
      return response.data;
    }

    // Other approaches use /documents endpoint
    const response = await axios.get(`${baseUrl}/documents`, {
      timeout: 180000,
      params: userId ? { user_id: userId } : {},
      headers: userId ? { 'X-User-Email': userId } : {}
    });
    return response.data;
  } catch (error) {
    console.error('Error listing knowledge base documents:', error);
    throw error;
  }
};

/**
 * Get universities by soft_fit_category from the universities knowledge base
 * Uses acceptance-rate based categories (not personalized fits)
 * @param {string} category - SAFETY, TARGET, REACH, or SUPER_REACH
 * @param {Array<string>} excludeIds - University IDs to exclude
 * @param {number} limit - Max results to return
 * @returns {Promise<{success: boolean, universities: Array}>}
 */
export const getUniversitiesByCategory = async (category, excludeIds = [], limit = 10) => {
  try {
    const baseUrl = import.meta.env.VITE_KNOWLEDGE_BASE_UNIVERSITIES_URL || 'https://knowledge-base-manager-universities-pfnwjfp26a-ue.a.run.app';

    // Build URL with fit_category filter - use server-side filtering
    // For REACH, include both REACH and SUPER_REACH categories
    const params = new URLSearchParams({
      limit: '200', // Get enough to have good selection after filtering
      fit_category: category === 'REACH' ? 'REACH' : category
    });

    const response = await axios.get(`${baseUrl}?${params}`, { timeout: 30000 });

    if (!response.data.success || !response.data.universities) {
      // If server-side filter returns nothing for REACH, try SUPER_REACH
      if (category === 'REACH') {
        const superReachParams = new URLSearchParams({
          limit: '100',
          fit_category: 'SUPER_REACH'
        });
        const superReachResponse = await axios.get(`${baseUrl}?${superReachParams}`, { timeout: 30000 });
        if (superReachResponse.data.success && superReachResponse.data.universities) {
          response.data = superReachResponse.data;
        }
      }
      if (!response.data.success) {
        return { success: false, universities: [], error: 'Failed to fetch universities' };
      }
    }

    // Normalize exclude IDs for comparison
    const normalizedExcludeIds = new Set(
      excludeIds.map(id => id.toLowerCase().replace(/[-\s]+/g, '_'))
    );

    // Filter to exclude already-added universities (server already filtered by category)
    let filtered = response.data.universities.filter(uni => {
      // Exclude already-added universities
      const normalizedId = (uni.university_id || '').toLowerCase().replace(/[-\s]+/g, '_');
      if (normalizedExcludeIds.has(normalizedId)) return false;

      return true;
    });

    // Sort by US News rank (lower is better, nulls last)
    filtered.sort((a, b) => {
      const rankA = a.us_news_rank ?? 999;
      const rankB = b.us_news_rank ?? 999;
      return rankA - rankB;
    });

    // Apply limit
    filtered = filtered.slice(0, limit);

    // Transform to match expected format
    const universities = filtered.map(uni => ({
      university_id: uni.university_id,
      university_name: uni.official_name,
      location: uni.location,
      acceptance_rate: uni.acceptance_rate,
      us_news_rank: uni.us_news_rank,
      soft_fit_category: uni.soft_fit_category,
      market_position: uni.market_position,
      summary: uni.summary
    }));

    console.log(`[API] Found ${universities.length} ${category} universities from KB`);

    return {
      success: true,
      universities: universities,
      total: universities.length,
      source: 'knowledge_base'
    };
  } catch (error) {
    console.error('Error fetching universities by category:', error);
    return { success: false, universities: [], error: error.message };
  }
};

/**
 * Get trust-labeled major facts for a university from the universities KB
 * (`?id=X&action=majors`). Every claim carries a `basis` (kb_verified /
 * kb_reported / opinion) and nulls mean "the school doesn't publish this".
 * @param {string} universityId - University ID
 * @param {Object} options - Optional filters
 * @param {string} options.college - Filter to one college within the university
 * @param {string} options.query - Server-side major-name filter
 * @returns {Promise<{success: boolean, colleges: Array, verification_status: string}>}
 */
/**
 * Applicant-reported admission outcomes per class year (#510) — aggregated
 * from public results communities, self-reported by applicants and
 * unverified; the UI must label it that way (and never link out to it).
 */
export const getReportedOutcomes = async (universityId) => {
  try {
    const baseUrl = import.meta.env.VITE_KNOWLEDGE_BASE_UNIVERSITIES_URL || 'https://knowledge-base-manager-universities-v2-pfnwjfp26a-ue.a.run.app';
    const response = await axios.get(baseUrl, {
      params: { id: universityId, action: 'reported-outcomes' }, timeout: 30000,
    });
    return response.data;
  } catch (error) {
    console.error('Error getting reported outcomes:', error);
    return { success: false, years: [], error: error.message };
  }
};

/**
 * The anonymized applicant records behind one major's reported tallies (#529)
 * — decision, round, GPA, SAT/ACT, APs, residency, first-gen, hooks per class
 * year. Self-reported and unverified; the UI must label it that way.
 */
/**
 * All anonymized applicant records for one class year (#548) — the profiles
 * behind the Explore card's per-year decision counts. Self-reported; label it.
 */
export const getYearReportedRecords = async (universityId, classYear) => {
  try {
    const baseUrl = import.meta.env.VITE_KNOWLEDGE_BASE_UNIVERSITIES_URL || 'https://knowledge-base-manager-universities-v2-pfnwjfp26a-ue.a.run.app';
    const response = await axios.get(baseUrl, {
      params: { id: universityId, action: 'year-records', year: classYear }, timeout: 30000,
    });
    return response.data;
  } catch (error) {
    console.error('Error getting year reported records:', error);
    return { success: false, records: [], error: error.message };
  }
};

export const getMajorReportedRecords = async (universityId, major, { statsOnly = false } = {}) => {
  try {
    const baseUrl = import.meta.env.VITE_KNOWLEDGE_BASE_UNIVERSITIES_URL || 'https://knowledge-base-manager-universities-v2-pfnwjfp26a-ue.a.run.app';
    const response = await axios.get(baseUrl, {
      // stats=1 filters to records with a GPA + test BEFORE the cap — the
      // scattergram's feed; without it the newest-first cap starves the chart.
      params: { id: universityId, action: 'major-records', major, ...(statsOnly ? { stats: '1' } : {}) },
      timeout: 30000,
    });
    return response.data;
  } catch (error) {
    console.error('Error getting major reported records:', error);
    return { success: false, records: [], error: error.message };
  }
};

export const getUniversityMajors = async (universityId, { college, query } = {}) => {
  try {
    // Fallback pins the LIVE v2 service (the non-v2 fallback used elsewhere in
    // this file predates the ES service going offline).
    const baseUrl = import.meta.env.VITE_KNOWLEDGE_BASE_UNIVERSITIES_URL || 'https://knowledge-base-manager-universities-v2-pfnwjfp26a-ue.a.run.app';
    const params = { id: universityId, action: 'majors' };
    if (college) params.college = college;
    if (query) params.q = query;

    const response = await axios.get(baseUrl, { params, timeout: 30000 });
    return response.data;
  } catch (error) {
    console.error('Error getting university majors:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Get one university's full KB document (`?id=X`) — everything the Discover
 * detail view renders. `-`/`_` in the id are interchangeable server-side.
 * @param {string} universityId - University ID
 * @returns {Promise<{success: boolean, university?: Object, error?: string}>}
 */
export const getUniversityById = async (universityId) => {
  try {
    // Fallback pins the LIVE v2 service (the non-v2 fallback used elsewhere in
    // this file predates the ES service going offline).
    const baseUrl = import.meta.env.VITE_KNOWLEDGE_BASE_UNIVERSITIES_URL || 'https://knowledge-base-manager-universities-v2-pfnwjfp26a-ue.a.run.app';
    const response = await axios.get(baseUrl, { params: { id: universityId }, timeout: 30000 });
    return response.data;
  } catch (error) {
    console.error('Error getting university:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Get the two-axis year history for a university (`?id=X&action=history`).
 * `snapshots` are Stratia KB per-cycle rows (application-CYCLE year axis,
 * authoritative); `reported_trends` are the school's own trend rows
 * (entering-class year axis, verified:false). The two use different year
 * conventions — never merge them into one timeline.
 * @param {string} universityId - University ID
 * @returns {Promise<{success: boolean, snapshots: Array, reported_trends: Array, notes: Array}>}
 */
export const getUniversityHistory = async (universityId) => {
  try {
    // Fallback pins the LIVE v2 service (the non-v2 fallback used elsewhere in
    // this file predates the ES service going offline).
    const baseUrl = import.meta.env.VITE_KNOWLEDGE_BASE_UNIVERSITIES_URL || 'https://knowledge-base-manager-universities-v2-pfnwjfp26a-ue.a.run.app';
    const response = await axios.get(baseUrl, { params: { id: universityId, action: 'history' }, timeout: 30000 });
    return response.data;
  } catch (error) {
    console.error('Error getting university history:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Delete a document from the knowledge base
 * Uses the knowledge base manager cloud function
 * Works with all approaches (RAG, Firestore, Elasticsearch) using the same endpoint
 * Note: Hybrid approach uses universities KB with DELETE method
 */
export const deleteKnowledgeBaseDocument = async (documentName, filename, userId) => {
  try {
    const approach = localStorage.getItem('knowledgeBaseApproach') || KNOWLEDGE_BASE_APPROACH;
    const baseUrl = getKnowledgeBaseUrl();

    // Hybrid approach uses DELETE method with university_id
    if (approach === 'hybrid') {
      const response = await axios.delete(baseUrl, {
        timeout: 180000,
        data: { university_id: documentName }
      });
      return response.data;
    }

    // Other approaches use POST to /delete endpoint
    const response = await axios.post(`${baseUrl}/delete`, {
      file_name: documentName,
      ...(userId && { user_id: userId })
    }, {
      timeout: 180000,
      headers: userId ? { 'X-User-Email': userId } : {}
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

/**
 * Get knowledge base document content for preview
 * Uses the knowledge base manager cloud function
 */
export const getKnowledgeBaseDocumentContent = async (fileName) => {
  try {
    const baseUrl = getKnowledgeBaseUrl();
    const response = await axios.post(`${baseUrl}/get-document-content`, {
      file_name: fileName
    }, {
      timeout: 60000
    });
    return response.data;
  } catch (error) {
    console.error('Error getting document content:', error);
    throw error;
  }
};

/**
 * Upload document to Vertex AI (via cloud function)
 * Uploads to GCS then indexes in Vertex AI RAG
 */
export const uploadVertexAIDocument = async (file, userId) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (userId) {
      formData.append('user_id', userId);
    }

    const baseUrl = VERTEXAI_KNOWLEDGE_BASE_URL;
    const response = await axios.post(`${baseUrl}/upload-document`, formData, {
      timeout: 120000,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(userId && { 'X-User-Email': userId })
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading Vertex AI document:', error);
    throw error;
  }
};

/**
 * Upload profile to Vertex AI (via cloud function)
 * Uploads to user-specific GCS folder then indexes in Vertex AI RAG
 */
export const uploadVertexAIProfile = async (file, userId) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (userId) {
      formData.append('user_id', userId);
    }

    const baseUrl = VERTEXAI_PROFILE_MANAGER_URL;
    const response = await axios.post(`${baseUrl}/upload-profile`, formData, {
      timeout: 120000,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...(userId && { 'X-User-Email': userId })
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading Vertex AI profile:', error);
    throw error;
  }
};

/**
 * List documents from Vertex AI (via cloud function)
 * Lists from GCS
 */
export const listVertexAIDocuments = async (userId) => {
  try {
    const baseUrl = VERTEXAI_KNOWLEDGE_BASE_URL;
    const response = await axios.get(`${baseUrl}/documents`, {
      timeout: 180000,
      params: userId ? { user_id: userId } : {},
      headers: userId ? { 'X-User-Email': userId } : {}
    });
    return response.data;
  } catch (error) {
    console.error('Error listing Vertex AI documents:', error);
    throw error;
  }
};

/**
 * List profiles from Vertex AI (via cloud function)
 * Lists from user-specific GCS folder
 */
export const listVertexAIProfiles = async (userId) => {
  try {
    const baseUrl = VERTEXAI_PROFILE_MANAGER_URL;
    const response = await axios.get(`${baseUrl}/profiles`, {
      timeout: 180000,
      params: { user_email: userId },
      headers: { 'X-User-Email': userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error listing Vertex AI profiles:', error);
    throw error;
  }
};

/**
 * Delete document from Vertex AI (via cloud function)
 * Deletes from both GCS and Vertex AI RAG
 */
export const deleteVertexAIDocument = async (documentName, displayName, userId) => {
  try {
    const baseUrl = VERTEXAI_KNOWLEDGE_BASE_URL;
    const response = await axios.post(`${baseUrl}/delete`, {
      file_name: documentName
    }, {
      timeout: 180000,
      headers: userId ? { 'X-User-Email': userId } : {}
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting Vertex AI document:', error);
    throw error;
  }
};

/**
 * Delete profile from Vertex AI (via cloud function)
 * Deletes from both GCS and Vertex AI RAG
 */
export const deleteVertexAIProfile = async (documentId, userId, displayName) => {
  try {
    const baseUrl = VERTEXAI_PROFILE_MANAGER_URL;
    const response = await axios.post(`${baseUrl}/delete`, {
      file_name: documentId
    }, {
      timeout: 180000,
      headers: { 'X-User-Email': userId }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting Vertex AI profile:', error);
    throw error;
  }
};

// ============================================
// College List Management API
// ============================================

/**
 * Get user's college list
 * @param {string} userEmail - User's email
 * @returns {Promise<{success: boolean, college_list: Array, count: number}>}
 */
export const getCollegeList = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/get-college-list`, {
      params: { user_email: userEmail },
      timeout: 180000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting college list:', error);
    throw error;
  }
};

/**
 * University branding for a set of ids — {id: {primary, secondary, logo_url}},
 * absent keys stay absent. The shared read behind the crest-and-colours rule
 * (skill `university-branding`): fetch per UNIVERSITY, never per row.
 */
export const getUniversityBranding = async (userEmail, universityIds) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/get-branding`, {
      params: { user_email: userEmail, ids: (universityIds || []).join(',') },
      timeout: 20000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data?.branding || {};
  } catch (error) {
    console.error('Error getting university branding:', error);
    return {};
  }
};

/**
 * Upcoming campus tours, department talks and webinars for the colleges on
 * the student's own list (#1326).
 *
 * Returns {events, gaps, checked, list_total}. `gaps` matters as much as
 * `events`: it names the colleges we found nothing upcoming for, so an empty
 * calendar can never be mistaken for "nothing is scheduled". A 404 means the
 * flag is off for this user — the page shouldn't be reachable anyway.
 */
export const getCampusEvents = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/get-campus-events`, {
      params: { user_email: userEmail },
      timeout: 60000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error fetching campus events:', error);
    return { success: false, events: [], gaps: [], checked: {}, list_total: 0 };
  }
};

/**
 * Book the Visit (2026-09-03) — a direct link to each college's own
 * visit-booking page for the colleges on the student's own list, replacing
 * the scraped open-slot calendar getCampusEvents fetched. Each row carries
 * {university_id, name, branding, visit_url, admissions_url,
 * has_direct_portal} — has_direct_portal tells the card whether visit_url
 * is a real booking portal or the fallback is just the general admissions
 * page. A 404 means the flag is off for this user.
 */
export const getVisitLinks = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/get-visit-links`, {
      params: { user_email: userEmail },
      timeout: 30000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error fetching visit links:', error);
    return { success: false, universities: [] };
  }
};

/**
 * Add or remove one chosen visit date from the student's plan (#1326).
 *
 * The plan is OUR record and books nothing — the student still registers with
 * the college. The UI says so; this is just where it is kept. Returns the
 * whole plan plus same-day clashes so the rail re-renders from one response.
 */
export const planCampusVisit = async (userEmail, { action, event, planKey, planKeys }) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/plan-campus-visit`, {
      user_email: userEmail, action, event, plan_key: planKey, plan_keys: planKeys,
    }, { timeout: 30000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    console.error('[API] Error updating visit plan:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add or remove a college from user's list
 * @param {string} userEmail - User's email
 * @param {string} action - 'add' or 'remove'
 * @param {{id: string, name: string}} university - University object
 * @param {string} intendedMajor - Student's intended major (optional)
 * @returns {Promise<{success: boolean, college_list: Array}>}
 */
export const updateCollegeList = async (userEmail, action, university, intendedMajor = '') => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/update-college-list`, {
      user_email: userEmail,
      action: action,
      university: university,
      intended_major: intendedMajor
    }, {
      timeout: 180000,
      headers: { 'X-User-Email': userEmail }
    });
    // No COLLEGE_ADDED here (audit 2026-08-09, F1): the only 'add' caller is
    // addSchoolWithAnalysis, whose addCollegeAnalysis call fires the event —
    // this one made every main-door add count twice.
    if (response.data?.success) notifyProgress();
    return response.data;
  } catch (error) {
    console.error('Error updating college list:', error);
    throw error;
  }
};

/**
 * Persist the student's per-school major decision (college_list/{id}.major_choice).
 * The server validates the school is on the list and match-binds the major
 * name against the KB's official list (matched:false = stored as given).
 * @param {string} userEmail - User's email
 * @param {string} universityId - University ID (must already be on the list)
 * @param {string} primaryMajor - The major the student will apply under
 * @param {Object} options - Optional fields
 * @param {string} options.backupMajor - Backup/alternate major
 * @param {string} options.rationale - Why this major
 * @returns {Promise<{success: boolean, major_choice: Object, near_misses: Array, note: string|null}>}
 */
export const setMajorChoice = async (userEmail, universityId, primaryMajor, { backupMajor, rationale } = {}) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const body = {
      user_email: userEmail,
      university_id: universityId,
      primary_major: primaryMajor,
      source: 'app'
    };
    // '' is MEANINGFUL: absent means keep the saved backup, empty means
    // clear it. A truthiness check would swallow the clear entirely.
    if (backupMajor !== undefined && backupMajor !== null) {
      body.backup_major = backupMajor;
    }
    if (rationale) body.rationale = rationale;

    const response = await axios.post(`${baseUrl}/set-major-choice`, body, {
      timeout: 30000,
      headers: { 'X-User-Email': userEmail }
    });
    // The Launchpad re-reads the list on this signal, so a major chosen in
    // Major Chances shows on the school's card on the way back (#4541).
    if (response.data?.success) notifyProgress();
    return response.data;
  } catch (error) {
    console.error('Error setting major choice:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Send / don't send her SAT/ACT to ONE test-optional school (#4747). Free.
 * The server refuses it (400) where the school is test-required or test-blind
 * or she has no score; the refusal's message comes back as `message`.
 * @returns {Promise<{success: boolean, send_scores?: boolean, score_choice?: Object, fit_needs_update?: boolean, message?: string}>}
 */
export const setScoreSending = async (userEmail, universityId, sendScores) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/set-score-sending`, {
      user_email: userEmail,
      university_id: universityId,
      send_scores: !!sendScores,
    }, { timeout: 30000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    console.error('Error setting score sending:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message,
      message: error.response?.data?.message,
    };
  }
};

/**
 * Persist the student's ranked candidate majors (≤5) — mirrors intended_major.
 * Free (no credit). Used by the Major Map card's "Set as my majors" action.
 * @param {string} userEmail - User's email
 * @param {Array<string>} majors - Ranked list of majors (max 5)
 * @param {string|null} primary - Optional primary (moves to front)
 * @returns {Promise<{success: boolean, intended_majors: Array, intended_major: string}>}
 */
export const setIntendedMajors = async (userEmail, majors, primary = null) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const body = { user_email: userEmail, majors };
    if (primary) body.primary = primary;
    const response = await axios.post(`${baseUrl}/set-intended-majors`, body, {
      timeout: 30000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error setting intended majors:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Get the student's saved Major Map (free).
 * `stale_parts` holds the raw fingerprint keys behind `stale_reasons`,
 * index-for-index, so a caller can decide per reason whether to show it.
 * @returns {Promise<{success: boolean, map: Object|null, stale: boolean, stale_parts: Array, stale_reasons: Array}>}
 */
export const getMajorMap = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/get-major-map`, {
      params: { user_email: userEmail },
      timeout: 30000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting major map:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// The Major Map "your colleges" lens (#1029): for each mapped major, which of
// the student's OWN listed colleges offer it. Free read; an outage returns an
// empty lens rather than an error, so callers can just not render it.
export const fetchMajorMapCoverage = async (userEmail) => {
    try {
        const response = await axios.get(`${getProfileManagerUrl()}/major-map-college-coverage`, {
            params: { user_email: userEmail },
            timeout: 15000,
            headers: { 'X-User-Email': userEmail },
        });
        return response.data?.coverage
            ? { coverage: response.data.coverage, listTotal: response.data.list_total || 0 }
            : { coverage: {}, listTotal: 0 };
    } catch {
        return { coverage: {}, listTotal: 0 };
    }
};

/** Has this user connected an AI agent (Claude/ChatGPT/Glean/Copilot) via the MCP connector?
 *  Read-only view over the connector's OAuth state: {connected, providers[]}.
 *  Failure degrades to connected:false — the UI keeps showing setup guidance. */
export const getAgentConnectionStatus = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/get-agent-connection-status`, {
      params: { user_email: userEmail },
      timeout: 15000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('Error getting agent connection status:', error);
    return { success: false, connected: false, providers: [] };
  }
};

/** Scratch-pad profile ingestion (review-first): extract structured fields
 *  from rough typed notes WITHOUT saving. The review screen saves via
 *  saveScratchpadProfile below. */
export const extractProfileScratchpad = async (userEmail, text) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/extract-profile-scratchpad`, {
      user_email: userEmail, text,
    }, { timeout: 90000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/** Save the reviewed scratch-pad fields (smart merge; raw notes persist as
 *  the provenance document). */
export const saveScratchpadProfile = async (userEmail, fields, sourceText) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/update-structured-profile`, {
      user_email: userEmail,
      profile_data: fields,
      source: 'scratchpad-notes.md',
      source_text: sourceText,
    }, { timeout: 60000, headers: { 'X-User-Email': userEmail } });
    // The Getting Started checklist observes this event — without it a
    // student who just told us about themselves watched 'Tell us about
    // you' sit unchecked until a full page reload.
    if (response.data?.success !== false) { notifyProgress(); warmFitsPrecompute(userEmail); }
    return response.data;
  } catch (error) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/** Cut the signed-in user's agent access server-side (all providers, or one
 *  of 'claude'/'chatgpt'). Works even when the AI client never calls our
 *  OAuth /revoke — the grants die here, so the agent's next call fails. */
export const revokeAgentConnections = async (userEmail, provider = null) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/revoke-agent-connections`, {
      user_email: userEmail,
      ...(provider ? { provider } : {}),
    }, { timeout: 15000, headers: { 'X-User-Email': userEmail } });
    if (response.data?.success) {
      // Launch buttons elsewhere share a 60s status cache — a disconnect
      // must not leave them saying "Connected" for up to a minute.
      _connCache = { email: null, at: 0, promise: null };
    }
    return response.data;
  } catch (error) {
    console.error('Error revoking agent connections:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// Module-level 60s cache so the many launch buttons on a page share ONE
// status request. Explicitly invalidated by TTL only — a connect happens in
// another tab, so the next page-load/expiry picks it up.
let _connCache = { email: null, at: 0, promise: null };
export const getAgentConnectionStatusCached = (userEmail) => {
  const now = Date.now();
  if (_connCache.email === userEmail && _connCache.promise && now - _connCache.at < 60000) {
    return _connCache.promise;
  }
  _connCache = { email: userEmail, at: now, promise: getAgentConnectionStatus(userEmail) };
  return _connCache.promise;
};

/** The user's saved agent prompt library (#332): custom categories/questions + favorites. */
export const getAgentPrompts = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/get-agent-prompts`, {
      params: { user_email: userEmail },
      timeout: 30000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('Error getting agent prompts:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/** Replace the user's agent prompt library (client owns the whole collection). */
export const saveAgentPrompts = async (userEmail, library) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/save-agent-prompts`, {
      user_email: userEmail,
      library,
    }, { timeout: 30000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    console.error('Error saving agent prompts:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Generate (or refresh) the student's Major Map. The SERVER charges 1 credit
 * per real generation (#284): an unchanged profile returns the cached map free,
 * 402 → {insufficientCredits: true}, 422 → {profileIncomplete: true, missing}.
 * Never deduct client-side around this call.
 * @param {string} userEmail - User's email
 * @param {boolean} force - Regenerate even when the profile hasn't changed
 */
export const generateMajorMap = async (userEmail, force = false) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/generate-major-map`, {
      user_email: userEmail,
      force
    }, {
      timeout: 120000,
      headers: { 'X-User-Email': userEmail }
    });
    if (response.data?.success) notifyProgress();
    return response.data;
  } catch (error) {
    if (error.response?.status === 402) {
      return {
        success: false,
        error: 'insufficient_credits',
        insufficientCredits: true,
        creditsRemaining: error.response?.data?.credits_remaining ?? 0
      };
    }
    if (error.response?.status === 422) {
      return {
        success: false,
        error: 'profile_incomplete',
        profileIncomplete: true,
        missing: error.response?.data?.missing || []
      };
    }
    console.error('Error generating major map:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Activity Ideas (free advisory): EC suggestions grounded in the profile +
 * Major Map. Fingerprint-cached server-side; force regenerates.
 */
export const generateActivityIdeas = async (userEmail, force = false) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/generate-activity-ideas`, {
      user_email: userEmail, force
    }, { timeout: 120000, headers: { 'X-User-Email': userEmail } });
    if (response.data?.success) notifyProgress();   // check off the Getting-Started step
    return response.data;
  } catch (error) {
    if (error.response?.status === 422) {
      return { success: false, error: 'profile_incomplete', profileIncomplete: true,
               missing: error.response?.data?.missing || [] };
    }
    console.error('Error generating activity ideas:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const getActivityIdeas = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/get-activity-ideas`, {
      params: { user_email: userEmail },
      timeout: 30000, headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting activity ideas:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Get the student's saved per-school major strategy (free).
 * stale = the KB has newer data than the strategy was built on.
 * @returns {Promise<{success: boolean, strategy: Object|null, stale: boolean, current_kb_year: number|null}>}
 */
export const getMajorStrategy = async (userEmail, universityId) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/get-major-strategy`, {
      params: { user_email: userEmail, university_id: universityId },
      timeout: 30000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting major strategy:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Generate the per-school major strategy. SERVER-billed (1 credit) on success;
 * a KB miss returns 200 {strategy: null, gaps} and is NEVER charged (#284).
 * 402 → {insufficientCredits: true}. Never deduct client-side.
 * @param {string} userEmail - User's email
 * @param {string} universityId - University ID
 * @param {Array<string>|null} majors - Optional override (max 4)
 */
export const generateMajorStrategy = async (userEmail, universityId, majors = null) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const body = { user_email: userEmail, university_id: universityId };
    if (majors && majors.length > 0) body.majors = majors;
    const response = await axios.post(`${baseUrl}/generate-major-strategy`, body, {
      timeout: 120000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 402) {
      return {
        success: false,
        error: 'insufficient_credits',
        insufficientCredits: true,
        creditsRemaining: error.response?.data?.credits_remaining ?? 0
      };
    }
    console.error('Error generating major strategy:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Get the student's saved per-college Major Chances ranking (free).
 * stale = the KB has newer data than the ranking was built on.
 * @returns {Promise<{success: boolean, ranking: Object|null, stale: boolean, current_kb_year: number|null}>}
 */
/**
 * Every saved major-chances ranking in ONE call, {universityId: ranking}.
 * The door constellation reads the whole list, and per-school calls cost a
 * round-trip each (founder: "this took sometime to load").
 */
export const getAllCollegeMajorChances = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/get-college-major-chances-bulk`, {
      params: { user_email: userEmail },
      timeout: 30000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting all college major chances:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const getCollegeMajorChances = async (userEmail, universityId) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/get-college-major-chances`, {
      params: { user_email: userEmail, university_id: universityId },
      timeout: 30000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting college major chances:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Rank the majors a college actually offers that fit the student, into
 * likelihood tiers (strong/possible/reach/long_shot). SERVER-billed (1 credit)
 * on success; a KB miss returns 200 {ranking: null, gaps} and is NEVER charged
 * (#302). 402 → {insufficientCredits: true}. Never deduct client-side.
 * @param {string} userEmail - User's email
 * @param {string} universityId - University ID
 */
export const rankCollegeMajors = async (userEmail, universityId) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/rank-college-majors`, {
      user_email: userEmail,
      university_id: universityId
    }, {
      timeout: 120000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 402) {
      return {
        success: false,
        error: 'insufficient_credits',
        insufficientCredits: true,
        creditsRemaining: error.response?.data?.credits_remaining ?? 0
      };
    }
    if (error.response?.status === 503) {
      // #298/#305 F5: credit-ledger read blip — retryable, not the upsell.
      return { success: false, error: 'credits_unavailable_retry', retryable: true };
    }
    console.error('Error ranking college majors:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Update fit analysis for a college in user's list
 * @param {string} userEmail - User's email
 * @param {string} universityId - University ID
 * @param {Object} fitAnalysis - Fit analysis data
 * @returns {Promise<{success: boolean}>}
 */
export const updateFitAnalysis = async (userEmail, universityId, fitAnalysis) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/update-fit-analysis`, {
      user_email: userEmail,
      university_id: universityId,
      fit_analysis: fitAnalysis
    }, {
      timeout: 180000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating fit analysis:', error);
    throw error;
  }
};

// ============================================
// Pre-computed Fit Matrix API
// ============================================

/**
 * Compute fit analysis for ALL universities and store in profile
 * This should be called after profile upload (runs in background)
 * @param {string} userEmail - User's email
 * @returns {Promise<{success: boolean, computed: number, fits_computed_at: string}>}
 */
// --- Odds warm-up ----------------------------------------------------------
// The whole-KB precompute takes minutes, and it used to START only when the
// student arrived at /universities — parking the wait exactly on top of the
// "see your odds" aha. Fire it the moment real profile content is saved, so by
// the time the student follows the next-step nudge the bands are ready (or
// nearly). Module-level guard: one in-flight pass, shared with any surface
// that would otherwise launch its own.
let fitsWarmupPromise = null;
export const getFitsWarmup = () => fitsWarmupPromise;
export const warmFitsPrecompute = (userEmail) => {
  if (!userEmail) return null;
  if (!fitsWarmupPromise) {
    // precompute-fit-previews is the REAL whole-KB pass (deterministic,
    // seconds, self-skips when the stored previews match the profile). The
    // previous target, /compute-all-fits, does not exist on profile-manager-v2
    // — every "warm" call 404ed silently and nothing was ever computed.
    fitsWarmupPromise = precomputeFitPreviews(userEmail)
      .catch(() => {})                                   // best-effort
      .finally(() => { fitsWarmupPromise = null; });
  }
  return fitsWarmupPromise;
};

// computeAllFits was removed here (digest 2026-07-28): it targeted
// /compute-all-fits, which does not exist on profile-manager-v2 — a stale
// bundle retried it into a 27x 404 error cluster. The real whole-KB pass is
// precomputeFitPreviews (see warmFitsPrecompute above). Nothing calls this
// shape any more; do not reintroduce it.

/**
 * Compute fit analysis for a SINGLE university (lazy fit computation with caching)
 * Called when a university is added to the Launchpad
 * The SERVER charges 1 credit per real compute (#285): cache hits are free,
 * and an empty balance returns 402 → {insufficientCredits: true, creditsRemaining}.
 * Do NOT deduct client-side around this call — that would double-charge.
 * @param {string} userEmail - User's email
 * @param {string} universityId - University ID to compute fit for
 * @param {boolean} forceRecompute - If true, bypass cache and force LLM recomputation
 * @param {string|null} intendedMajor - Optional explicit major override; the fit doc
 *                                      stamps intended_major_used/_source in response
 * @returns {Promise<{success: boolean, fit_analysis: object, from_cache: boolean, credits_remaining?: number}>}
 */
export const computeSingleFit = async (userEmail, universityId, forceRecompute = false, intendedMajor = null) => {
  try {
    console.log(`[API] Computing single fit for ${userEmail} - ${universityId} (force=${forceRecompute})...`);
    const baseUrl = getProfileManagerUrl();
    const body = {
      user_email: userEmail,
      university_id: universityId,
      force_recompute: forceRecompute
    };
    if (intendedMajor) body.intended_major = intendedMajor;
    const response = await axios.post(`${baseUrl}/compute-single-fit`, body, {
      timeout: 120000,  // match connector/QA budgets — a shorter client abort after the server computes+bills means paying twice (#296 review F3)
      headers: { 'X-User-Email': userEmail }
    });
    console.log(`[API] Computed single fit (from_cache=${response.data.from_cache}):`, response.data);
    if (response.data?.success && !response.data?.from_cache) {
      track(EVENTS.FIT_ANALYSIS_RUN, { university_id: universityId });
    }
    if (response.data?.success) notifyProgress();
    return response.data;
  } catch (error) {
    // Check for 402 Payment Required (insufficient credits)
    if (error.response?.status === 402) {
      console.warn('[API] fit analysis refused by the ledger (402)');
      return {
        success: false,
        error: 'insufficient_credits',
        insufficientCredits: true,
        creditsRemaining: error.response?.data?.credits_remaining ?? 0,
        // The server's own 402 text is never shown (#4668: it names the
        // ledger); the client owns the words.
        message: 'This full read comes with the Season Pass.'
      };
    }
    if (error.response?.status === 422 && error.response?.data?.error === 'profile_incomplete') {
      // #4064: an empty record gets no paid band and is not charged; the
      // server says what to add, and callers show `error` as written.
      return { success: false, error: error.response.data.message, code: 'profile_incomplete',
               missing: error.response.data.missing || [] };
    }
    console.error('Error computing single fit:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Bundled add-college analysis (#310): ONE credit generates BOTH the fit
 * analysis AND the per-college major-chances ranking (server-billed). Adding a
 * college to the Launchpad always uses this — the server does the 402, so keep
 * the hasCredits pre-check + CreditsUpgradeModal but NEVER deduct client-side.
 * `force` regenerates both artifacts (used by the card's regenerate). A
 * KB-majors miss returns {major_chances: null, note} — not a second charge and
 * not a failure; the fit is still generated.
 * 402 → {insufficientCredits: true, creditsRemaining}; 503 → {retryable: true}.
 * @param {string} userEmail
 * @param {string} universityId
 * @param {boolean} force - regenerate both artifacts (bypass any cache)
 * @returns {Promise<{success, fit_analysis, major_chances, note?, credits_remaining?}>}
 */
/**
 * Find similar schools for the student's major (free, deterministic). Returns
 * rigor-band peers that offer the major neighbourhood through an honest door,
 * each with the student's free soft-fit band. `settings` (optional) overrides
 * and persists the student's preferences (band_width, institution_type, etc.).
 */
export const findSimilarSchools = async (userEmail, universityId, { major, settings } = {}) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/similar-schools`, {
      user_email: userEmail, university_id: universityId,
      ...(major ? { major } : {}), ...(settings ? { settings } : {}),
    }, { timeout: 30000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    console.error('Error finding similar schools:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/** Read the student's persisted similar-schools preferences (or defaults). */
export const getSimilarSchoolsSettings = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/similar-schools-settings`,
      { user_email: userEmail }, { headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/** Persist default similar-schools preferences from the Settings page. */
export const saveSimilarSchoolsSettings = async (userEmail, settings) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/similar-schools-settings`,
      { user_email: userEmail, settings }, { headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const addCollegeAnalysis = async (userEmail, universityId, force = false, { door } = {}) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/add-college-analysis`, {
      user_email: userEmail,
      university_id: universityId,
      force,
      // Which door the add came from (#5085): the server's first_list_read
      // event carries it, so the funnel splits /start's onboarding out.
      ...(door ? { door } : {}),
    }, {
      timeout: 120000,  // matches compute-single-fit — a shorter abort after the server bills means paying twice
      headers: { 'X-User-Email': userEmail }
    });
    // This bundled add-college path is how most fits actually run —
    // computeSingleFit (which also tracks) only covers regeneration.
    if (response.data?.success && !response.data?.from_cache) {
      // force=true is the Launchpad's regenerate — a fit refresh on a school
      // ALREADY on the list. It was firing college_added on every refresh
      // (audit F3) and mislabelling the run's source.
      track(EVENTS.FIT_ANALYSIS_RUN, { university_id: universityId,
        source: force ? 'regenerate' : 'add_college' });
      if (!force) {
        // The activation milestone (digest S1, 2026-07-30) — now fired from
        // exactly ONE place for every add path (audit F1).
        track(EVENTS.COLLEGE_ADDED, { university_id: universityId, source: 'add_college_analysis' });
      }
    }
    // The checklist's final step checks off a computed fit. The shared add
    // flow fires notifyProgress on the LIST write (step 1), but the fit only
    // exists after THIS call — and the checklist card lives in the persistent
    // AppLayout, so no navigation ever remounts it. Without this, the bar
    // froze one event behind reality on the journey's last step.
    if (response.data?.success) notifyProgress();
    return response.data;
  } catch (error) {
    if (error.response?.status === 402) {
      return {
        success: false,
        error: 'insufficient_credits',
        insufficientCredits: true,
        creditsRemaining: error.response?.data?.credits_remaining ?? 0,
        message: "This school's full read comes with the Season Pass."
      };
    }
    if (error.response?.status === 503) {
      // #298: credit-ledger read blip — retryable, not the upsell modal.
      return { success: false, error: 'credits_unavailable_retry', retryable: true };
    }
    if (error.response?.status === 422 && error.response?.data?.error === 'profile_incomplete') {
      return { success: false, error: error.response.data.message, code: 'profile_incomplete',
               missing: error.response.data.missing || [] };
    }
    console.error('Error adding college analysis:', error);
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Generate fit infographic image using Gemini AI
 * Creates a personalized visual infographic for the student's fit analysis
 * @param {string} userEmail - User's email
 * @param {string} universityId - University ID to generate infographic for
 * @param {boolean} forceRegenerate - If true, bypass cache and regenerate image
 * @returns {Promise<{success: boolean, infographic_url: string, from_cache: boolean}>}
 */
export const generateFitInfographic = async (userEmail, universityId, forceRegenerate = false) => {
  try {
    console.log(`[API] Generating fit infographic for ${userEmail} - ${universityId} (force=${forceRegenerate})...`);
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/generate-fit-image`, {
      user_email: userEmail,
      university_id: universityId,
      force_regenerate: forceRegenerate
    }, {
      timeout: 120000,  // 2 min timeout for image generation
      headers: { 'X-User-Email': userEmail }
    });
    console.log(`[API] Generated infographic (from_cache=${response.data.from_cache}):`, response.data);
    return response.data;
  } catch (error) {
    console.error('Error generating fit infographic:', error);
    return { success: false, error: error.message };
  }
};


/**
 * Get structured infographic data for frontend rendering
 * Returns JSON that can be rendered as a beautiful native component (no image generation)
 * @param {string} userEmail - User's email
 * @param {string} universityId - University ID to get infographic data for
 * @returns {Promise<{success: boolean, infographic_data: Object}>}
 */
export const getFitInfographicData = async (userEmail, universityId) => {
  try {
    console.log(`[API] Getting fit infographic data for ${userEmail} - ${universityId}...`);
    const baseUrl = getProfileManagerUrl();
    const response = await axios.get(`${baseUrl}/generate-fit-infographic-data`, {
      params: {
        user_email: userEmail,
        university_id: universityId
      },
      timeout: 30000,
      headers: { 'X-User-Email': userEmail }
    });
    console.log(`[API] Got infographic data:`, response.data);
    return response.data;
  } catch (error) {
    console.error('Error getting fit infographic data:', error);
    return { success: false, error: error.message };
  }
};


/**
 * Get pre-computed fits with optional filtering
 * @param {string} userEmail - User's email
 * @param {Object} filters - Optional filters: { category, state, exclude_ids }
 * @param {number} limit - Max results to return
 * @param {string} sortBy - Sort order: "rank" or "match_score"
 * @returns {Promise<{success: boolean, results: Array, total: number, fits_ready: boolean}>}
 */
// Personalized, deterministic fit candidates — the SAME precompute the agent's
// list_fit_candidates uses (student's stats vs each admitted class, in-state
// rate, graded major impaction, profile-aware ceiling), bucketed by band. Powers
// the Launchpad Discover flow so manual add and agent add agree.
export const getFitCandidates = async (userEmail, band = null, { state = null, universityType = null, limit = 60 } = {}) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/fit-candidates`, {
      user_email: userEmail,
      band,
      state,
      type: universityType,
      limit,
    }, {
      timeout: 30000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('Error getting fit candidates:', error);
    return { success: false, candidates: [] };
  }
};

// Auto-recharge (#438) — read/flip the toggle via profile_manager's AUTHENTICATED
// proxy (NOT payment_manager directly; the charge/toggle endpoints there are
// internal-only). profile_manager's #223 gate binds the request to the verified
// user, so a header can't arm someone else's card.
export const getAutoRecharge = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const r = await axios.get(`${baseUrl}/auto-recharge`, {
      params: { user_email: userEmail },
      headers: { 'X-User-Email': userEmail },
      timeout: 15000,
    });
    return r.data;
  } catch (error) {
    console.error('Error reading auto-recharge:', error);
    return { success: false, enabled: false, eligible: false };
  }
};

export const setAutoRecharge = async (userEmail, enabled) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const r = await axios.post(`${baseUrl}/auto-recharge`,
      { user_email: userEmail, enabled: !!enabled },
      { headers: { 'X-User-Email': userEmail }, timeout: 15000 });
    return r.data;
  } catch (error) {
    console.error('Error setting auto-recharge:', error);
    return { success: false };
  }
};

export const getPrecomputedFits = async (userEmail, filters = {}, limit = 20, sortBy = 'rank') => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/get-fits`, {
      user_email: userEmail,
      filters: filters,
      limit: limit,
      sort_by: sortBy
    }, {
      timeout: 30000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting pre-computed fits:', error);
    throw error;
  }
};

// recomputeLaunchpadFits was removed (#296 review F1): it bulk-recomputed
// every Launchpad fit (force=true, fire-and-forget) after profile edits.
// Once /compute-single-fit bills server-side (#285), that loop silently
// drained credits with no consent UI. Fits now recompute only on explicit,
// priced user actions; KB-drift staleness banners cover the nudge.
/**
 * Check if fits are ready for a user
 * @param {string} userEmail - User's email
 * @returns {Promise<{ready: boolean, fits_computed_at: string|null}>}
 */
export const checkFitsReady = async (userEmail) => {
  try {
    const result = await getPrecomputedFits(userEmail, {}, 1);
    return {
      ready: result.fits_ready === true,
      computed_at: result.fits_computed_at || null,
      total: result.total || 0
    };
  } catch (error) {
    return { ready: false, computed_at: null, total: 0 };
  }
};

/**
 * Get fits filtered by category (for Smart Discovery quick actions)
 * @param {string} userEmail - User's email
 * @param {string} category - Fit category: SAFETY, TARGET, REACH, SUPER_REACH (or null for all)
 * @param {string} state - State filter (e.g., "CA") or null
 * @param {Array<string>} excludeIds - University IDs to exclude
 * @param {number} limit - Max results
 * @returns {Promise<{success: boolean, results: Array, total: number}>}
 */
export const getFitsByCategory = async (userEmail, category = null, state = null, excludeIds = [], limit = 10) => {
  const filters = {};
  if (category) filters.category = category;
  if (state) filters.state = state;
  if (excludeIds && excludeIds.length > 0) filters.exclude_ids = excludeIds;

  return getPrecomputedFits(userEmail, filters, limit, 'rank');
};

/**
 * Get a balanced college list with safety, target, and reach schools
 * Fetches multiple categories in parallel for optimal performance
 * @param {string} userEmail - User's email
 * @param {Array<string>} excludeIds - University IDs to exclude
 * @returns {Promise<{success: boolean, results: Array, total: number, breakdown: Object}>}
 */
export const getBalancedList = async (userEmail, excludeIds = []) => {
  try {
    // Fetch fits for each category in parallel
    const [safetyResult, targetResult, reachResult] = await Promise.all([
      getFitsByCategory(userEmail, 'SAFETY', null, excludeIds, 3),
      getFitsByCategory(userEmail, 'TARGET', null, excludeIds, 4),
      getFitsByCategory(userEmail, 'REACH', null, excludeIds, 3)
    ]);

    // Merge results
    const balanced = [
      ...(safetyResult.results || []),
      ...(targetResult.results || []),
      ...(reachResult.results || [])
    ];

    return {
      success: true,
      results: balanced,
      total: balanced.length,
      breakdown: {
        safety: safetyResult.results?.length || 0,
        target: targetResult.results?.length || 0,
        reach: reachResult.results?.length || 0
      }
    };
  } catch (error) {
    console.error('Error getting balanced list:', error);
    return {
      success: false,
      error: error.message,
      results: [],
      total: 0
    };
  }
};


/**
 * Check if fit recomputation is needed — profile-driven flags PLUS the
 * KB-staleness diff (kb_updates) from the dedicated backend endpoint
 * (design DESIGN-kb-refresh-fit-staleness.md §2).
 * @param {string} userEmail - User's email
 * @returns {Promise<{needs_recomputation: boolean, reason: string|null, changes: array, kb_updates: array}>}
 */
export const checkFitRecomputationNeeded = async (userEmail) => {
  const empty = { needs_recomputation: false, reason: null, changes: [], kb_updates: [] };
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/check-fit-recomputation`, {
      user_email: userEmail
    }, {
      headers: { 'X-User-Email': userEmail }
    });

    if (response.data.success) {
      return {
        needs_recomputation: response.data.needs_recomputation === true,
        reason: response.data.reason || null,
        changes: response.data.changes || [],
        profile_updated_at: response.data.profile_updated_at || null,
        fits_computed_at: response.data.fits_computed_at || null,
        kb_updates: response.data.kb_updates || []
      };
    }
    return empty;
  } catch (error) {
    // Best-effort: this only powers the "your fits may be stale" banners, so a
    // 401 during the cold-load auth race or a transient network error is
    // expected — keep those quiet. Surface anything unexpected as a warning.
    const status = error?.response?.status;
    if (status === 401 || !error?.response) {
      console.debug('fit-recomputation check skipped:', status || error?.message);
    } else {
      console.warn('Error checking fit recomputation status:', error?.message || error);
    }
    return empty;
  }
};

/**
 * Archived prior-cycle fit analyses for a university (newest first).
 * @returns {Promise<array>} history entries ({history_key, fit_category, ...})
 */
export const getFitHistory = async (userEmail, universityId) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/get-fit-history`, {
      user_email: userEmail,
      university_id: universityId
    }, {
      headers: { 'X-User-Email': userEmail }
    });
    return response.data.success ? (response.data.history || []) : [];
  } catch (error) {
    console.error('Error fetching fit history:', error);
    return [];
  }
};


// ============== CREDITS API ==============

/**
 * Get user's credit balance and tier info
 * @param {string} userEmail - User's email
 * @returns {Promise<{success: boolean, credits: object}>}
 */
export const getUserCredits = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    // #5195: a sign-up moment carried to the new account's first read of
    // its plan, so the server's account_created counts it (a closed list
    // there; anything else reads as none).
    const moment = heldMomentId();
    const response = await axios.get(`${baseUrl}/get-credits`, {
      params: { user_email: userEmail, ...(moment ? { signup_moment: moment } : {}) },
      headers: { 'X-User-Email': userEmail }
    });

    // Normalize response: V2 returns flat object, Frontend expects { success: true, credits: {...} }
    const data = response.data;
    if (data.credits_remaining !== undefined && !data.success) {
      return {
        success: true,
        credits: data
      };
    }
    return data;
  } catch (error) {
    console.error('Error reading the plan ledger:', error);
    return {
      success: false,
      error: error.message,
      credits: {
        tier: 'free',
        credits_remaining: 0,
        credits_total: 0
      }
    };
  }
};

/**
 * Check if user has enough credits for an operation
 * @param {string} userEmail - User's email
 * @param {number} creditsNeeded - Number of credits required
 * @returns {Promise<{has_credits: boolean, credits_remaining: number}>}
 */
export const checkCredits = async (userEmail, creditsNeeded = 1) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/check-credits`, {
      user_email: userEmail,
      credits_needed: creditsNeeded
    }, {
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error checking the plan ledger:', error);
    return {
      success: false,
      has_credits: false,
      credits_remaining: 0,
      error: error.message
    };
  }
};

// upgradeSubscription() and addUserCredits() were removed in #316: granting a
// subscription tier or credits from the browser is a self-serve billing bypass
// (a user could mint themselves a Season Pass / credits for free). Those grants
// are authoritative ONLY in payment_manager_v2's signature-verified Stripe
// webhook, and the /upgrade-subscription + /add-credits backend endpoints are
// now admin-token gated. PaymentSuccess just re-reads the webhook-updated state.

/**
 * Deduct credits from user's balance
 * @param {string} userEmail - User's email
 * @param {number} creditCount - Number of credits to deduct (default 1)
 * @param {string} reason - Reason for deduction (e.g., 'fit_analysis', 'infographic_regeneration')
 * @returns {Promise<{success: boolean, credits_remaining: number}>}
 */
export const deductCredit = async (userEmail, creditCount = 1, reason = 'infographic_regeneration') => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/deduct-credit`, {
      user_email: userEmail,
      credit_count: creditCount,
      reason: reason
    }, {
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('Error charging the plan ledger:', error);
    return {
      success: false,
      error: error.message,
      credits_remaining: 0
    };
  }
};

// ============== END CREDITS API ==============

// ============== EMAIL API ==============

/**
 * Send welcome email to new user
 * @param {string} userEmail - User's email
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const sendWelcomeEmail = async (userEmail) => {
  try {
    const baseUrl = getProfileManagerUrl();
    const response = await axios.post(`${baseUrl}/send-welcome-email`, {
      user_email: userEmail
    }, {
      headers: { 'X-User-Email': userEmail }
    });
    console.log('[API] Welcome email sent successfully to', userEmail);
    return response.data;
  } catch (error) {
    console.error('[API] Error sending welcome email:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// ============== END EMAIL API ==============

// ============== COUNSELOR AGENT API ==============

/**
 * Curated summer-programs catalog + deterministic per-student
 * recommendations (#462). One GET returns everything the Summer Programs
 * page needs: {programs, recommendations, rising_grade, count}.
 */
export const getSummerPrograms = async (userEmail) => {
  try {
    const response = await axios.get(`${COUNSELOR_AGENT_URL}/summer-programs`, {
      params: { user_email: userEmail },
      timeout: 30000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error fetching summer programs:', error);
    return { success: false, error: error.message, programs: [], recommendations: [] };
  }
};

/**
 * Fetch the semester roadmap for a user.
 *
 * The backend resolver (counselor_agent /roadmap) honors caller-provided
 * (gradeLevel, semester) when BOTH are passed and resolve cleanly; otherwise
 * it falls back to inferring from profile.graduation_year + the current month.
 * Pass either both or neither — passing only one is treated as "neither" by
 * the resolver and falls back to the profile path.
 */
export const fetchStudentRoadmap = async (userEmail, gradeLevel, semester) => {
  try {
    const body = { user_email: userEmail };
    if (gradeLevel) body.grade_level = gradeLevel;
    if (semester) body.semester = semester;
    const response = await axios.post(`${COUNSELOR_AGENT_URL}/roadmap`, body);
    return response.data;
  } catch (error) {
    console.error('[API] Error fetching roadmap:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Chat with the Intelligent Counselor Agent
 * Uses /chat endpoint on counselor-agent
 */
export const fetchCounselorChat = async (userEmail, message, history = []) => {
  try {
    const response = await axios.post(`${COUNSELOR_AGENT_URL}/chat`, {
      user_email: userEmail,
      message: message,
      history: history
    }, {
      timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error in counselor chat:', error);
    return { success: false, error: error.message, reply: "I'm having trouble connecting right now." };
  }
};

/**
 * The Concierge — a counseling agent with named, switchable conversation
 * threads (founder 2026-08-02: "store different conversation threads and
 * rename them... pick up whatever thread he wants or start a new one").
 * Cross-thread preferences (memory/insights) load into every thread the
 * same way; only the transcript itself is per-thread.
 */

/**
 * One turn: {reply, profile_updates_applied, ready_for_next_step, celebrate,
 *  celebrate_cta, suggestions, thread_id, thread_title?, restored?, history?}.
 * `kickoff: true` tells the backend this is a panel-open turn on a NEW,
 * empty thread — real conversations should be opened via
 * getConciergeThreadMessages instead (a free read); kickoff is only for
 * generating a genuine first greeting.
 * Same COUNSELOR_AGENT_URL / auth shape as fetchCounselorChat.
 */
// #2033: two live turns finished at 32.7s and 34.3s (200) after the client
// had given up at 30s — the answer existed, the student saw an error, the
// work was wasted. The worst turn on record is 42s; the budget sits above
// the tail. The sequential tool-round tail itself is #1457.
const CONCIERGE_CHAT_TIMEOUT_MS = 90000;

// axios 1.x reports BOTH a timeout and a navigation-abort as ECONNABORTED
// (clarifyTimeoutError is off); only the message tells them apart, and a
// student leaving the page mid-turn is not a slow backend (reviewer, #2035).
const isTimeout = (error) => /timeout of \d+ms exceeded/i.test(String(error?.message || ''));

export const conciergeChat = async (userEmail, message, history = [],
                                    { kickoff = false, threadId = null,
                                      selectedSchoolIds = null,
                                      uploaded = null, from = null } = {}) => {
  const startedAt = Date.now();
  try {
    const response = await axios.post(`${COUNSELOR_AGENT_URL}/concierge-chat`, {
      user_email: userEmail, message, history, kickoff, thread_id: threadId,
      // #1197: the picker knows every row's university_id, so the add path
      // sends them verbatim instead of a prose sentence for the model to
      // re-parse. Omitted entirely on every other turn — the server treats
      // its presence as "this turn is a batch-add proposal", nothing else.
      ...(selectedSchoolIds?.length ? { selected_school_ids: selectedSchoolIds } : {}),
      // Chat upload acknowledgment: the file already went through the
      // profile-builder pipeline; this only names it. The server verifies
      // each name against the profile's own uploaded_files before use.
      ...(uploaded?.length ? { uploaded } : {}),
      // a label, never text (#5258): the turn came from "Students are asking"
      ...(from === 'popular' ? { from: 'popular' } : {}),
    }, {
      timeout: CONCIERGE_CHAT_TIMEOUT_MS,
      headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error in concierge chat:', error);
    if (isTimeout(error)) {
      try {
        track(EVENTS.CONCIERGE_TIMEOUT, {
          route: 'concierge-chat', timeout_ms: CONCIERGE_CHAT_TIMEOUT_MS,
          elapsed_ms: Date.now() - startedAt,
        });
      } catch { /* analytics never breaks the conversation */ }
      return { success: false, error: error.message,
               reply: 'That one took too long to answer. Ask again in a moment.' };
    }
    return { success: false, error: error.message,
             reply: "I'm having trouble connecting right now — try again in a moment." };
  }
};

// The streamed turn (#2555). The server sends NDJSON:
// {"delta": text} while the reply is written (each piece already passed the
// honesty guards), then {"final": <the same response conciergeChat returns>}.
// `onDelta` gets each piece; the return value is the final response. Anything
// that goes wrong before the final line falls back to the ordinary request,
// so a student always gets an answer.
export const conciergeChatStream = async (userEmail, message, history = [],
                                          opts = {}, onDelta = () => {}) => {
  const { kickoff = false, threadId = null, from = null } = opts;
  // `accepted`: the stream route answered 200, so the server IS running this
  // turn. From then on a second request would run a second turn (review
  // 2026-09-30: a turn that never streams, or a connection dropped after
  // the server finished), so only a refusal before that falls back.
  let accepted = false;
  try {
    const res = await authedFetch(`${COUNSELOR_AGENT_URL}/concierge-chat-stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail },
      body: JSON.stringify({ user_email: userEmail, message, history, kickoff, thread_id: threadId,
        ...(from === 'popular' ? { from: 'popular' } : {}) }),
    });
    if (!res.ok || !res.body || typeof res.body.getReader !== 'function') {
      throw new Error(`stream unavailable (${res.status})`);
    }
    accepted = true;
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (value) buffer += decoder.decode(value, { stream: !done });
      let nl;
      while ((nl = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, nl).trim();
        buffer = buffer.slice(nl + 1);
        if (!line) continue;
        const item = JSON.parse(line);
        if (typeof item.delta === 'string') {
          try { onDelta(item.delta); } catch { /* a render error never ends the turn */ }
        } else if (item.reset) {
          // The server fell back mid-stream (#2555): what showed was
          // guard-clean but the final reply is a different one. Clear it.
          try { onDelta('', { reset: true }); } catch { /* never ends the turn */ }
        } else if (item.final) {
          return item.final;
        }
      }
      if (done) break;
    }
    throw new Error('stream ended without a final response');
  } catch (error) {
    // Refused before the turn started: the ordinary request answers instead.
    // Once accepted, the turn reports the failure honestly instead of
    // running again (its reply, if it finished, is in the thread).
    if (!accepted) {
      console.warn('[API] concierge stream unavailable; falling back:', error?.message);
      return conciergeChat(userEmail, message, history, opts);
    }
    console.error('[API] concierge stream broke mid-reply:', error);
    return { success: false, error: error?.message || 'stream interrupted',
             reply: "I lost the connection partway through. Ask again and I'll pick it up." };
  }
};

// One tap on a reply (founder 2026-08-06). Fire-and-forget by design: the
// student's feedback must never block or break their conversation.
export const conciergeFeedback = async (userEmail, { threadId, turnIndex, rating, reason, replyKey }) => {
  try {
    const response = await axios.post(`${COUNSELOR_AGENT_URL}/concierge-feedback`, {
      user_email: userEmail, thread_id: threadId, turn_index: turnIndex, rating,
      ...(reason ? { reason } : {}),
      ...(replyKey ? { reply_key: replyKey } : {}),
    }, { timeout: 10000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    console.warn('[API] concierge feedback failed', error?.message);
    return { success: false };
  }
};

/** {success, threads: [{id, title, updated_at, message_count, university_id,
 * university_name}]} — every conversation this student has ever had with the
 * Concierge, newest first. `universityId` narrows to the conversations filed
 * under one school (#1617, the card's Research drawer). */
export const listConciergeThreads = async (userEmail, { universityId = null } = {}) => {
  const response = await axios.get(`${COUNSELOR_AGENT_URL}/concierge-threads`, {
    params: { user_email: userEmail, ...(universityId ? { university_id: universityId } : {}) },
    headers: { 'X-User-Email': userEmail },
  });
  return response.data;
};

/** {success, id, title, university_id, university_name} — a fresh, empty
 * thread, optionally filed under one university (#1617). */
export const createConciergeThread = async (userEmail, scope = null) => {
  const response = await axios.post(`${COUNSELOR_AGENT_URL}/concierge-thread-create`,
    {
      user_email: userEmail,
      ...(scope?.university_id ? {
        university_id: scope.university_id,
        university_name: scope.university_name || null,
      } : {}),
      ...(scope?.topic ? { topic: scope.topic } : {}),
    },
    { headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail } });
  return response.data;
};

export const renameConciergeThread = async (userEmail, threadId, title) => {
  const response = await axios.post(`${COUNSELOR_AGENT_URL}/concierge-thread-rename`,
    { user_email: userEmail, thread_id: threadId, title },
    { headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail } });
  return response.data;
};

export const deleteConciergeThread = async (userEmail, threadId) => {
  const response = await axios.post(`${COUNSELOR_AGENT_URL}/concierge-thread-delete`,
    { user_email: userEmail, thread_id: threadId },
    { headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail } });
  return response.data;
};

/** The landing conversation brought into the account at sign-in (#2945).
 * op 'import' {import_key, messages, schools} -> {success, id, title,
 * questions, already}; op 'archive' / 'restore' {thread_id} is Start fresh
 * and its undo. Never throws: a refusal or a network failure resolves
 * {success:false, reason, status}. */
export const conciergeThreadImport = async (userEmail, body) => {
  try {
    const response = await axios.post(`${COUNSELOR_AGENT_URL}/concierge-thread-import`,
      { user_email: userEmail, ...body },
      { headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    const data = error?.response?.data || {};
    return { ...data, success: false, status: error?.response?.status || 0,
      reason: data.reason || (error?.response ? 'refused' : 'network') };
  }
};

/** {success, id, title, messages: [{role:'student'|'concierge', text}]} — a
 * pure, free read (no LLM call). This is how switching threads works. */
export const getConciergeThreadMessages = async (userEmail, threadId) => {
  const response = await axios.get(`${COUNSELOR_AGENT_URL}/concierge-thread-messages`, {
    params: { user_email: userEmail, thread_id: threadId },
    headers: { 'X-User-Email': userEmail },
  });
  return response.data;
};

/** Execute an agentic pending action the STUDENT confirmed (ADR 0004). The
 * nonce is the only client input — the tool and its args were frozen
 * server-side at proposal time, single-use, short TTL. */
export const confirmConciergeAction = async (userEmail, nonce, threadId = null) => {
  const response = await axios.post(`${COUNSELOR_AGENT_URL}/concierge-action-confirm`,
    { user_email: userEmail, nonce, ...(threadId ? { thread_id: threadId } : {}) },
    { headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail } });
  return response.data;
};

/** Where an Astra add batch stands (#2938): each school's state (queued,
 * fit, chances, ready, failed) and the bar's step counts, read from the
 * batch doc profile-manager's run writes. `stalled` means a run is owed and
 * none holds the lease. */
export const getAddBatchStatus = async (userEmail, batchId) => {
  const response = await axios.get(`${getProfileManagerUrl()}/add-batch-status`, {
    params: { user_email: userEmail, batch_id: batchId },
    timeout: 15000,
    headers: { 'X-User-Email': userEmail },
  });
  return response.data;
};

/** The student's recent Astra add batches (#2942): every batch still running,
 * plus any that settled in the last few minutes, each in the same shape
 * add-batch-status returns. The Launchpad reads this to show each card's
 * analysis while it runs, wherever the student added from. */
export const getActiveAddBatches = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/add-batch-active`, {
    params: { user_email: userEmail },
    timeout: 15000,
    headers: { 'X-User-Email': userEmail },
  });
  return response.data;
};

/** Ask for a stalled batch's run (#2938). The run is leased server-side, so a
 * request while one is live is a no-op; never awaited by the poller. */
export const runAddBatch = async (userEmail, batchId) => {
  const response = await axios.post(`${getProfileManagerUrl()}/add-batch-run`,
    { user_email: userEmail, batch_id: batchId },
    { timeout: 300000, headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail } });
  return response.data;
};

/** Decline a pending agentic action (best-effort; the server TTL also
 * expires unanswered cards). */
export const declineConciergeAction = async (userEmail, nonce) => {
  const response = await axios.post(`${COUNSELOR_AGENT_URL}/concierge-action-decline`,
    { user_email: userEmail, nonce },
    { headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail } });
  return response.data;
};

// ── Concierge flows (admin-only flowchart, #1177) ──────────────────────────
// The task flows the Concierge runs, as DATA: code defaults with Firestore
// overrides applied. Admin-gated server-side (a non-admin gets a 404, never a
// map of the machine), so these ride authedFetch with the signed-in email
// claimed in the header — the same shape the marketing console uses.
const _flowJson = async (res) => {
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.success === false) {
    throw new Error(body.error || `Flow request failed (${res.status})`);
  }
  return body;
};

/** {success, flows: [{id, title, enabled, intro, triggers[], steps[], action}]} */
export const listConciergeFlows = async () =>
  _flowJson(await authedFetch(`${COUNSELOR_AGENT_URL}/concierge-flows`, { headers: _mktHeaders() }));

/** Save an admin override. The patch carries only copy-shaped keys — title,
 * enabled, intro, triggers[], steps[{field, ask, why}] (order = flow order).
 * The terminal action is never editable. Returns {success, flow}. */
export const saveConciergeFlow = async (id, patch) =>
  _flowJson(await authedFetch(`${COUNSELOR_AGENT_URL}/concierge-flow-save`, {
    method: 'POST', headers: _mktHeaders(), body: JSON.stringify({ id, patch: patch || {} }),
  }));

/**
 * Mark a roadmap task as complete/incomplete
 * Uses /mark-task endpoint on counselor-agent
 */
export const markRoadmapTask = async (userEmail, taskId, completed = true) => {
  try {
    const response = await axios.post(`${COUNSELOR_AGENT_URL}/mark-task`, {
      user_email: userEmail,
      task_id: taskId,
      completed: completed
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error marking task:', error);
    return { success: false, error: error.message };
  }
};

// ==================== COUNSELOR CHAT HISTORY ====================

/**
 * Save counselor chat conversation
 */
export const saveCounselorChat = async (userEmail, conversationId, messages, title = null) => {
  try {
    const response = await axios.post(`${COUNSELOR_AGENT_URL}/save-chat`, {
      user_email: userEmail,
      conversation_id: conversationId,
      messages: messages,
      title: title
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error saving counselor chat:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Load a specific counselor chat conversation
 */
export const loadCounselorChat = async (userEmail, conversationId) => {
  try {
    const response = await axios.get(`${COUNSELOR_AGENT_URL}/load-chat`, {
      params: {
        user_email: userEmail,
        conversation_id: conversationId
      },
      timeout: 10000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error loading counselor chat:', error);
    return { success: false, error: error.message };
  }
};

/**
 * List counselor chat conversations
 */
export const listCounselorChats = async (userEmail, limit = 20) => {
  try {
    const response = await axios.get(`${COUNSELOR_AGENT_URL}/list-chats`, {
      params: {
        user_email: userEmail,
        limit: limit
      },
      timeout: 10000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error listing counselor chats:', error);
    return { success: false, conversations: [] };
  }
};

/**
 * Get the most recent active counselor conversation
 */
export const getActiveCounselorChat = async (userEmail) => {
  try {
    const response = await axios.get(`${COUNSELOR_AGENT_URL}/get-active-chat`, {
      params: { user_email: userEmail },
      timeout: 10000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error getting active chat:', error);
    return { success: false, conversation: null };
  }
};

// ==================== ROADMAP TASKS ====================

/**
 * Generate personalized roadmap tasks from university data
 */
export const generateRoadmapTasks = async (userEmail) => {
  try {
    const response = await axios.post(`${COUNSELOR_AGENT_URL}/generate-tasks`, {
      user_email: userEmail,
      save: true
    }, {
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error generating tasks:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user's roadmap tasks
 * @param {string} userEmail 
 * @param {string} status - Optional: 'pending', 'completed', 'in_progress'
 * @param {string} universityId - Optional: filter by university
 */
export const getRoadmapTasks = async (userEmail, status = null, universityId = null) => {
  try {
    const params = { user_email: userEmail };
    if (status) params.status = status;
    if (universityId) params.university_id = universityId;

    const response = await axios.get(`${COUNSELOR_AGENT_URL}/get-tasks`, {
      params: params,
      timeout: 10000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error getting roadmap tasks:', error);
    return { success: false, tasks: [] };
  }
};

// ============== RESEARCH NOTEBOOK ==============
// Research artifacts saved from Claude (via the MCP connector) or the app,
// stored at users/{email}/research on profile_manager_v2.

/**
 * List the user's saved research notes (newest first).
 * @param {string} userEmail
 * @param {{kind?: string, universityId?: string}} [opts]
 * @returns {Promise<{success: boolean, research: object[], count?: number}>}
 */
export const listResearch = async (userEmail, { kind = null, universityId = null } = {}) => {
  try {
    const params = { user_email: userEmail };
    if (kind) params.kind = kind;
    if (universityId) params.university_id = universityId;
    const response = await axios.get(`${getProfileManagerUrl()}/get-research`, {
      params,
      timeout: 15000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error listing research:', error);
    return { success: false, research: [] };
  }
};

/**
 * Cross-user Popular Workflows (aggregate tool-sequence signatures + run counts;
 * no user data). Newest/most-run first.
 * @returns {Promise<{success: boolean, workflows: object[], count?: number}>}
 */
export const getPopularWorkflows = async (userEmail, limit = 20) => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/get-popular-workflows`, {
      params: { limit },
      timeout: 15000,
      headers: userEmail ? { 'X-User-Email': userEmail } : {},
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error getting popular workflows:', error);
    return { success: false, workflows: [] };
  }
};

/**
 * Get one research note in full (includes body_markdown + provenance).
 * @returns {Promise<{success: boolean, research: object|null}>}
 */
export const getResearch = async (userEmail, researchId) => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/get-research`, {
      params: { user_email: userEmail, research_id: researchId },
      timeout: 15000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error getting research:', error);
    return { success: false, research: null };
  }
};

/**
 * Create/save a research note from the app.
 * @param {string} userEmail
 * @param {{title: string, bodyMarkdown: string, kind?: string, summary?: string,
 *   universityIds?: string[], tags?: string[]}} input
 */
export const saveResearch = async (userEmail, input) => {
  if (!userEmail || !input?.title?.trim() || !input?.bodyMarkdown?.trim()) {
    return { success: false, error: 'user_email, title and bodyMarkdown are required' };
  }
  try {
    trackResearchSaved(input.kind);
    const body = {
      user_email: userEmail,
      title: input.title.trim(),
      body_markdown: input.bodyMarkdown,
      kind: input.kind || 'note',
      summary: input.summary || '',
      university_ids: input.universityIds || [],
      tags: input.tags || [],
      source: 'app',
    };
    const response = await axios.post(`${getProfileManagerUrl()}/save-research`, body, {
      timeout: 15000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error saving research:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a research note.
 * @returns {Promise<{success: boolean, research_id?: string}>}
 */
export const deleteResearch = async (userEmail, researchId) => {
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/delete-research`,
      { user_email: userEmail, research_id: researchId },
      { timeout: 15000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    console.error('[API] Error deleting research:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update fields of an existing research note (partial — only provided fields change).
 * @param {string} userEmail
 * @param {string} researchId
 * @param {{title?: string, summary?: string, bodyMarkdown?: string, kind?: string,
 *   universityIds?: string[], tags?: string[]}} patch
 */
export const updateResearch = async (userEmail, researchId, patch = {}) => {
  if (!userEmail || !researchId) {
    return { success: false, error: 'user_email and research_id are required' };
  }
  try {
    const body = { user_email: userEmail, research_id: researchId };
    if (patch.title !== undefined) body.title = patch.title;
    if (patch.summary !== undefined) body.summary = patch.summary;
    if (patch.bodyMarkdown !== undefined) body.body_markdown = patch.bodyMarkdown;
    if (patch.kind !== undefined) body.kind = patch.kind;
    if (patch.universityIds !== undefined) body.university_ids = patch.universityIds;
    if (patch.tags !== undefined) body.tags = patch.tags;
    if (patch.pinned !== undefined) body.pinned = patch.pinned;
    if (patch.actions !== undefined) body.actions = patch.actions;
    const response = await axios.post(`${getProfileManagerUrl()}/update-research`, body, {
      timeout: 15000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error updating research:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Pin or unpin a research note (thin wrapper over updateResearch). The backend
 * already persists `pinned` on the research doc; this surfaces it to the app.
 * @returns {Promise<{success: boolean, research?: object}>}
 */
export const pinResearch = async (userEmail, researchId, pinned = true) =>
  updateResearch(userEmail, researchId, { pinned });

/**
 * Decision Ledger: each college with its predicted fit category + recorded
 * admission decision (predicted-vs-actual). Read-only; no credits.
 * @returns {Promise<{success: boolean, outcomes: object[], decided_count: number, total: number}>}
 */
export const getOutcomeCalibration = async (userEmail) => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/get-outcome-calibration`, {
      params: { user_email: userEmail },
      timeout: 15000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error getting outcome calibration:', error);
    return { success: false, outcomes: [], decided_count: 0, total: 0 };
  }
};

/**
 * Record (or clear, with '') a college's admission decision — the OUTCOME field,
 * kept separate from the application process status.
 * @returns {Promise<{success: boolean}>}
 */
export const setApplicationDecision = async (userEmail, universityId, decision, appliedMajor, appliedRound) => {
  try {
    const body = { user_email: userEmail, university_id: universityId, decision: decision || '' };
    // Bundle the major-applied + round (#489/#ledger) when the caller has them so
    // a decision click records a complete row without extra writes.
    if (appliedMajor !== undefined) body.applied_major = appliedMajor || '';
    if (appliedRound !== undefined) body.applied_round = appliedRound || '';
    const response = await axios.post(`${getProfileManagerUrl()}/update-application-status`,
      body,
      { timeout: 15000, headers: { 'X-User-Email': userEmail } });
    if (response.data?.success && decision) track(EVENTS.OUTCOME_LOGGED, { decision });
    return response.data;
  } catch (error) {
    console.error('[API] Error setting application decision:', error);
    return { success: false, error: error.message };
  }
};

/** Record just the major the student applied to at a college (#489). */
export const setAppliedMajor = async (userEmail, universityId, appliedMajor) => {
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/update-application-status`,
      { user_email: userEmail, university_id: universityId, applied_major: appliedMajor || '' },
      { timeout: 15000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    console.error('[API] Error setting applied major:', error);
    return { success: false, error: error.message };
  }
};

/** Record just the application round the student applied under (#ledger). */
export const setAppliedRound = async (userEmail, universityId, appliedRound) => {
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/update-application-status`,
      { user_email: userEmail, university_id: universityId, applied_round: appliedRound || '' },
      { timeout: 15000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    console.error('[API] Error setting applied round:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a user-authored task in the user's roadmap_tasks subcollection.
 * Stamps `created_by: 'user'` so future surfaces can distinguish manually
 * added tasks from template-translated ones. Uses the existing generic
 * /save-roadmap-task endpoint on profile_manager_v2 — no new backend.
 *
 * @param {string} userEmail
 * @param {{title: string, dueDate?: string, universityId?: string, notes?: string,
 *   description?: string, phaseIndex?: number, category?: string,
 *   actionKey?: string, requestedOn?: string, taskId?: string,
 *   visitKey?: string}} input
 *   `taskId` makes the write idempotent — pass it whenever the task has a
 *   real-world identity (a campus visit is one event, however many times the
 *   student presses add).
 *   `dueDate` is YYYY-MM-DD or null. `universityId` is one of the user's
 *   college_list ids, optional.
 * @returns {Promise<{success: boolean, task_id: string, error?: string}>}
 */
/**
 * A caller-supplied task identity, reduced to something Firestore will accept
 * as a document id: no '/', no '.'-only names, bounded length. Returns '' when
 * nothing usable is left, so the caller falls back to a fresh random id rather
 * than writing to a doc named after the empty string.
 */
export const sanitizeTaskId = (value) => {
  const cleaned = String(value == null ? '' : value)
    .replace(/[^A-Za-z0-9_.:-]/g, '_')
    .replace(/^\.+$/, '')
    .slice(0, 180);
  return cleaned.trim();
};

export const createUserTask = async (userEmail, input) => {
  if (!userEmail || !input?.title?.trim()) {
    return { success: false, error: 'user_email and a non-empty title are required' };
  }
  // A caller that knows the task's real-world IDENTITY passes it as `taskId`,
  // and the write becomes idempotent: /save-roadmap-task merges onto that doc,
  // so pressing "add" twice updates one task instead of minting a second.
  // This is the campus-visit duplicate the founder screenshotted on
  // 2026-08-14 — the same tour listed twice, because the only guard was a
  // flag written in a SECOND round trip after the tasks had already landed.
  // Without an identity: timestamp + random nibble, as before.
  const taskId = sanitizeTaskId(input.taskId)
    || `user_task_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const taskData = {
    title: input.title.trim(),
    status: 'pending',
    type: 'user',
    created_by: 'user',
  };
  if (input.dueDate) taskData.due_date = input.dueDate;
  if (Number.isInteger(input.phaseIndex)) taskData.phase_index = input.phaseIndex;
  // What the task involves — the timeline renders it as the row's subtitle.
  // Distinct from `notes`, which is the running commentary added afterwards.
  if (input.description) taskData.description = input.description;
  if (input.universityId) taskData.university_id = input.universityId;
  if (input.notes) taskData.notes = input.notes;
  // e.g. 'summer_program' — lets the timeline retire dead annual cycles
  // instead of showing them overdue forever (2026-07-31). Also
  // 'fit_action' / 'transcript' / 'campus_visit' / 'essay', which is how the
  // Launchpad readiness card knows what a school-tagged task IS (2026-08-13).
  // 'essay' is the one the student's own status drives: a supplement written
  // in Google Docs is invisible to the tracker, so the roadmap task is what
  // decides essay readiness (founder, 2026-08-14).
  if (input.category) taskData.category = input.category;
  // Which fit recommendation this task came from. Without it an adopted
  // pointer would be offered again on the next render, because the card
  // could not tell that this task IS that suggestion.
  if (input.actionKey) taskData.action_key = input.actionKey;
  // Which campus-visit event this task IS — the events page's plan key
  // (school + kind + start instant). Stored so the read side can recognise
  // duplicates written before the task id carried the identity.
  if (input.visitKey) taskData.visit_key = input.visitKey;
  // When the student says they ordered a transcript. Recorded, never
  // inferred — "requested" is a claim only they can make.
  if (input.requestedOn) taskData.requested_on = input.requestedOn;
  // Structured payload a caller needs stored verbatim — e.g. the brag sheet's
  // {teacher, class_name, sheet}. Merged AFTER the named fields but with the
  // identity fields protected, so a caller can never rename or re-status a
  // task through the back door.
  if (input.taskData && typeof input.taskData === 'object') {
    Object.entries(input.taskData).forEach(([k, v]) => {
      if (['title', 'status', 'type', 'created_by'].includes(k)) return;
      taskData[k] = v;
    });
  }

  try {
    const response = await axios.post(
      `${getProfileManagerUrl()}/save-roadmap-task`,
      { user_email: userEmail, task_id: taskId, task_data: taskData },
      {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': userEmail,
        },
      }
    );
    return { ...response.data, task_id: response.data?.task_id || taskId };
  } catch (error) {
    console.error('[API] Error creating user task:', error);
    return {
      success: false,
      task_id: taskId,
      error: error?.response?.data?.error || error.message || 'Failed to create task',
    };
  }
};

/**
 * Patch an EXISTING roadmap task in place.
 *
 * Same endpoint as createUserTask, same task_id, a narrow task_data: the
 * backend writes with `merge=True`, so only the fields named here change and
 * everything else on the task survives. This is how a recommendation task
 * gains `asked_on` without the client having to re-send (and therefore risk
 * flattening) the brag sheet it already carries.
 *
 * @param {string} userEmail
 * @param {string} taskId          the EXISTING task's id — never a new one
 * @param {object} patch           the fields to merge, e.g. {asked_on: '2026-08-14'}
 * @returns {Promise<{success: boolean, task_id: string, error?: string}>}
 */
export const updateUserTask = async (userEmail, taskId, patch) => {
  if (!userEmail || !taskId || !patch || typeof patch !== 'object' || !Object.keys(patch).length) {
    return { success: false, task_id: taskId, error: 'user_email, task_id and a patch are required' };
  }
  try {
    const response = await axios.post(
      `${getProfileManagerUrl()}/save-roadmap-task`,
      { user_email: userEmail, task_id: taskId, task_data: patch },
      {
        timeout: 10000,
        headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail },
      }
    );
    return { ...response.data, task_id: response.data?.task_id || taskId };
  } catch (error) {
    console.error('[API] Error updating user task:', error);
    return {
      success: false,
      task_id: taskId,
      error: error?.response?.data?.error || error.message || 'Failed to update task',
    };
  }
};

/**
 * Fetch the unified "This Week" focus list — the top urgent items across
 * roadmap_tasks, essays, scholarships, and college deadlines for a user.
 * Server-side composes, sorts, and applies urgency thresholds; the client
 * just renders.
 *
 * @returns {Promise<{success: boolean, items: Array, total: number}>}
 *   On error, returns { success: false, items: [], total: 0 } so callers
 *   can render an empty state without special-casing failures.
 */
export const fetchWorkFeed = async (userEmail, limit = 8) => {
  try {
    const response = await axios.get(`${COUNSELOR_AGENT_URL}/work-feed`, {
      params: { user_email: userEmail, limit },
      timeout: 15000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error fetching work feed:', error);
    return { success: false, items: [], total: 0 };
  }
};

/**
 * Fetch the unified Roadmap timeline: ONE graduation-anchored, phase-grouped
 * feed of everything to do (milestones + college deadlines + essays +
 * scholarships + tasks). Superset of /work-feed — keeps past-due items and
 * adds synthetic milestones, no cap.
 *
 * @returns {Promise<{success, graduation_year, needs_graduation_year,
 *   phases: Array<{phase, phase_index, items: Array}>, flat: Array, total}>}
 *   On error, returns a safe empty shape so callers can render an empty state.
 */
/**
 * Season moments (#2144): the four-rung ladder the Launchpad strip shows,
 * computed by counselor_agent from the same feeds readiness uses. A 404 is
 * the flag being off for this account; every failure resolves to
 * {success:false} so the caller renders absence, never a confident empty
 * ladder.
 */
export const getSeasonMoments = async (userEmail) => {
  try {
    const response = await axios.get(`${COUNSELOR_AGENT_URL}/season-moments`, {
      params: { user_email: userEmail, recorded_by: 'launchpad' },
      timeout: 30000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    if (error?.response?.status !== 404) console.error('[API] Error fetching season moments:', error);
    return { success: false, moments: [], next: null };
  }
};

/**
 * "Students are asking" on signed-in Astra's opening (#5258): the counted
 * rank and the first two school names on the student's own list. 404 while
 * astra_popular_questions is off for this account; any failure is
 * {success:false}, which the shelf renders as nothing.
 */
export const getAstraPopular = async (userEmail) => {
  try {
    const response = await axios.get(`${COUNSELOR_AGENT_URL}/astra-popular`, {
      params: { user_email: userEmail },
      timeout: 15000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    if (error?.response?.status !== 404) console.error('[API] Error fetching popular questions:', error?.message);
    return { success: false };
  }
};

export const fetchTimeline = async (userEmail) => {
  try {
    const response = await axios.get(`${COUNSELOR_AGENT_URL}/timeline`, {
      params: { user_email: userEmail },
      // 30s, not 15: a cold counselor instance + a long college list ran
      // 9-10s server-side and crossed 15s often enough that the roadmap
      // "failed to load all the time" (founder, 2026-08-11). The server is
      // also faster now (deadline cache + parallel profile/enrichment), but
      // the client must not declare failure on a build that is finishing.
      timeout: 30000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error fetching timeline:', error);
    return {
      success: false, graduation_year: null, needs_graduation_year: false,
      phases: [], flat: [], total: 0,
    };
  }
};

/**
 * Run the LLM tailoring pass over the roadmap timeline (#483). Slow (a real
 * Gemini generation) — the timeline renders deterministically first and this
 * fires in the background when enrichment_status !== 'fresh', then the caller
 * refetches. Free to the student by design.
 */
/**
 * Re-plan ONE roadmap item into another timeframe (phaseIndex 0-11), or pass
 * null for "Anytime" (no committed timeframe). The server owns what a move
 * means — which kinds may move, and what happens to a dated task's due date —
 * so the UI never has to reimplement the phase math.
 */
export const moveRoadmapItem = async (userEmail, itemId, phaseIndex) => {
  if (!userEmail || !itemId) {
    return { success: false, error: 'user_email and item_id are required' };
  }
  try {
    const response = await axios.post(`${COUNSELOR_AGENT_URL}/move-roadmap-item`,
      { user_email: userEmail, item_id: itemId,
        phase_index: Number.isInteger(phaseIndex) ? phaseIndex : null },
      { timeout: 15000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    console.error('[API] Error moving roadmap item:', error);
    return {
      success: false,
      error: error?.response?.data?.error || error.message || 'Failed to move the item',
    };
  }
};

export const enrichTimeline = async (userEmail) => {
  try {
    const response = await axios.post(`${COUNSELOR_AGENT_URL}/enrich-timeline`,
      { user_email: userEmail },
      { timeout: 120000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    console.error('[API] Error enriching timeline:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Write the `notes` field on a user-owned Firestore document. Powers the
 * inline NotesAffordance across the consolidated Roadmap UI.
 *
 * `collection` must be one of the whitelisted notes-bearing collections
 * (server enforces NOTES_COLLECTIONS): roadmap_tasks, essay_tracker,
 * scholarship_tracker, college_list, aid_packages.
 *
 * Empty string clears the notes. Returns the structured response from
 * the endpoint, or { success: false, error } on transport failure so
 * callers can show an error toast and revert their optimistic update.
 */
export const updateNotes = async (userEmail, collection, itemId, notes) => {
  try {
    const response = await axios.post(
      `${getProfileManagerUrl()}/update-notes`,
      {
        user_email: userEmail,
        collection,
        item_id: itemId,
        notes,
      },
      {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': userEmail,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('[API] Error updating notes:', error);
    const serverMessage = error?.response?.data?.error;
    return {
      success: false,
      error: serverMessage || error.message || 'Failed to save notes',
    };
  }
};

/**
 * Status overlay for computed roadmap items (milestones, counselor picks,
 * summer picks): 'todo' | 'in_progress' | 'completed'. Tasks use
 * updateTaskStatus (their doc is the source of truth).
 */
export const setRoadmapItemStatus = async (userEmail, itemId, status) => {
  try {
    const response = await axios.post(`${PROFILE_MANAGER_URL}/set-roadmap-item-status`, {
      user_email: userEmail,
      item_id: itemId,
      status,
    }, {
      timeout: 10000,
      headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('Error setting roadmap item status:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update task status
 */
export const updateTaskStatus = async (userEmail, taskId, status) => {
  try {
    const response = await axios.post(`${PROFILE_MANAGER_URL}/update-task-status`, {
      user_email: userEmail,
      task_id: taskId,
      status: status
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error updating task status:', error);
    return { success: false, error: error.message };
  }
};

// ==================== APPLICATION STATUS ====================

/**
 * Update application status for a university
 */
export const updateApplicationStatus = async (userEmail, universityId, statusData) => {
  try {
    const response = await axios.post(`${PROFILE_MANAGER_URL}/update-application-status`, {
      user_email: userEmail,
      university_id: universityId,
      ...statusData
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error updating application status:', error);
    return { success: false, error: error.message };
  }
};

// ==================== ESSAYS ====================

/**
 * Save essay draft
 */
export const saveEssay = async (userEmail, essayId, essayData) => {
  try {
    const response = await axios.post(`${PROFILE_MANAGER_URL}/save-essay`, {
      user_email: userEmail,
      essay_id: essayId,
      ...essayData
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error saving essay:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all essays for user
 */
export const getEssays = async (userEmail, universityId = null) => {
  try {
    const params = { user_email: userEmail };
    if (universityId) params.university_id = universityId;

    const response = await axios.get(`${PROFILE_MANAGER_URL}/get-essays`, {
      params: params,
      timeout: 10000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error getting essays:', error);
    return { success: false, essays: [] };
  }
};

/**
 * Update essay status
 */
export const updateEssayStatus = async (userEmail, essayId, status) => {
  try {
    const response = await axios.post(`${PROFILE_MANAGER_URL}/update-essay-status`, {
      user_email: userEmail,
      essay_id: essayId,
      status: status
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error updating essay status:', error);
    return { success: false, error: error.message };
  }
};

// ==================== FINANCIAL AID ====================

/**
 * Save financial aid package for a university
 */
export const saveAidPackage = async (userEmail, universityId, aidData) => {
  try {
    const response = await axios.post(`${PROFILE_MANAGER_URL}/save-aid-package`, {
      user_email: userEmail,
      university_id: universityId,
      ...aidData
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error saving aid package:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all financial aid packages for user
 */
export const getAidPackages = async (userEmail) => {
  try {
    const response = await axios.get(`${PROFILE_MANAGER_URL}/get-aid-packages`, {
      params: { user_email: userEmail },
      timeout: 10000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error getting aid packages:', error);
    return { success: false, packages: [] };
  }
};


// ==================== WHAT FAMILIES LIKE YOURS PAID (#5492) ====================
// Behind net_cost_estimate: every route 404s while the flag is off. A failed
// call resolves {success: false} (the page shows the standard line, never an
// empty list read as "no schools"). Never deduct client-side: the server
// decides what a run costs.

export const getCostEstimates = async (userEmail) => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/get-cost-estimates`, {
      params: { user_email: userEmail },
      timeout: 20000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    return { success: false, error: error.response?.data?.error || 'request_failed' };
  }
};

export const saveFamilyFinances = async (userEmail, answers) => {
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/save-family-finances`,
      { user_email: userEmail, ...answers },
      { timeout: 15000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    const data = error.response?.data || {};
    return { success: false, error: data.error || 'request_failed', fields: data.fields || null };
  }
};

export const clearFamilyFinances = async (userEmail) => {
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/clear-family-finances`,
      { user_email: userEmail },
      { timeout: 15000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    return { success: false, error: error.response?.data?.error || 'request_failed' };
  }
};

/** The ranges for the whole list. 402 -> {insufficientCredits: true}. */
export const computeCostEstimates = async (userEmail) => {
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/compute-cost-estimates`,
      { user_email: userEmail },
      { timeout: 60000, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    if (error.response?.status === 402) {
      return {
        success: false,
        error: 'insufficient_credits',
        insufficientCredits: true,
        creditsRemaining: error.response?.data?.credits_remaining ?? 0
      };
    }
    return { success: false, error: error.response?.data?.error || 'request_failed' };
  }
};


// ==================== ESSAY TRACKER (Auto-populated from college list) ====================

/**
 * Sync essay prompts from user's college list to their essay tracker
 * This pulls essay prompts from all universities in the user's college list
 */
export const syncEssayTracker = async (userEmail) => {
  try {
    const url = getProfileManagerUrl();
    const response = await axios.post(`${url}/sync-essay-tracker`, {
      user_email: userEmail
    }, {
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error syncing essay tracker:', error);
    return { success: false, error: error.message, essays: [] };
  }
};

/**
 * Get all essays from the essay tracker
 */
export const getEssayTracker = async (userEmail) => {
  try {
    const url = getProfileManagerUrl();
    const response = await axios.get(`${url}/get-essay-tracker`, {
      params: { user_email: userEmail },
      timeout: 10000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error getting essay tracker:', error);
    return { success: false, essays: [] };
  }
};

/**
 * Update essay progress (status, content, word count)
 */
export const updateEssayProgress = async (userEmail, essayId, updates) => {
  try {
    const url = getProfileManagerUrl();
    const response = await axios.post(`${url}/update-essay-progress`, {
      user_email: userEmail,
      essay_id: essayId,
      ...updates
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error updating essay progress:', error);
    return { success: false, error: error.message };
  }
};

// ==================== SCHOLARSHIP TRACKER (Auto-populated from college list) ====================

/**
 * Sync scholarships from user's college list to their scholarship tracker
 * This pulls available scholarships from all universities in the user's college list
 */
export const syncScholarshipTracker = async (userEmail) => {
  try {
    const url = getProfileManagerUrl();
    const response = await axios.post(`${url}/sync-scholarship-tracker`, {
      user_email: userEmail
    }, {
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error syncing scholarship tracker:', error);
    return { success: false, error: error.message, scholarships: [] };
  }
};

/**
 * Get all scholarships from the scholarship tracker
 */
export const getScholarshipTracker = async (userEmail) => {
  try {
    const url = getProfileManagerUrl();
    const response = await axios.get(`${url}/get-scholarship-tracker`, {
      params: { user_email: userEmail },
      timeout: 10000,
      headers: { 'X-User-Email': userEmail }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error getting scholarship tracker:', error);
    return { success: false, scholarships: [] };
  }
};

/**
 * Update scholarship application status
 */
export const updateScholarshipStatus = async (userEmail, scholarshipId, status, notes = null) => {
  try {
    const url = getProfileManagerUrl();
    const response = await axios.post(`${url}/update-scholarship-status`, {
      user_email: userEmail,
      scholarship_id: scholarshipId,
      status,
      notes
    }, {
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'X-User-Email': userEmail
      }
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error updating scholarship status:', error);
    return { success: false, error: error.message };
  }
};


// In-app "Send feedback" (#364). Posts to the same contact_form function the
// public Contact page uses; the backend attaches the optional screenshot to the
// support email and labels feedback distinctly (source: 'in-app').
const CONTACT_API_URL = import.meta.env.VITE_CONTACT_API_URL || 'https://contact-form-pfnwjfp26a-ue.a.run.app';

export const sendFeedback = async ({ name, email, category, message, page, screenshot }) => {
  try {
    const res = await fetch(CONTACT_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: name || 'Stratia user',
        email: email || 'unknown@stratiaadmissions.com',
        subject: category && category !== 'General' ? `Feedback — ${category}` : 'Feedback',
        message: (message || '').trim(),
        source: 'in-app',
        page: page || '',
        screenshot: screenshot || null,
      }),
    });
    const data = await res.json().catch(() => ({}));
    return res.ok ? { success: true, ...data } : { success: false, error: data?.error || 'Send failed' };
  } catch (err) {
    return { success: false, error: 'Network error — please try again.' };
  }
};


// ==================== TESTIMONIALS (honest social proof) ====================
// Real, consented student quotes only — no fabricated or placeholder
// testimonials anywhere. Submission is authed (the axios interceptor attaches
// the Firebase token for profile-manager URLs); the public reader returns only
// admin-approved entries and is safe on the signed-out landing page.

/**
 * Submit a testimonial from the signed-in user. `consent` must be true — the
 * backend rejects unconsented submissions; the UI never sends one. Nothing the
 * user submits is shown publicly until an admin approves it.
 */
// Smiley rating + writeup, SAVED to the app_feedback collection (with a
// best-effort inbox notification server-side). The older sendFeedback() only
// emailed — feedback lived in an inbox, not the system.
export const submitAppFeedback = async ({ rating, verdict, message, praise, improve, page, context, contactEmail, shareOk, likedFeatures, grantToken }) => {
  // Works signed-OUT (the outreach email links straight to /feedback — the
  // route is auth-exempt server-side, like the public Contact form). Identity
  // rides along when the user IS signed in; anonymous users may leave an
  // optional contact address.
  const email = firebaseAuth?.currentUser?.email || null;
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/submit-feedback`,
      {
        user_email: email, rating: rating ?? null, verdict: verdict || null,
        message: message || null,
        praise: praise || null, improve: improve || null, page, context,
        contact_email: contactEmail || null, share_ok: shareOk === true,
        liked_features: Array.isArray(likedFeatures) ? likedFeatures : [],
        // Present only when they arrived from the credits email. The server
        // still requires a VERIFIED identity before any credits move — this
        // token just names which offer they're answering.
        grant_token: grantToken || null,
      },
      { headers: email ? { 'X-User-Email': email } : {} });
    return response.data;
  } catch (error) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const submitTestimonial = async ({ quote, display_name, role, consent, context }) => {
  const email = firebaseAuth?.currentUser?.email;
  const response = await axios.post(`${getProfileManagerUrl()}/submit-testimonial`,
    { quote, display_name, role, consent, context },
    { timeout: 15000, headers: email ? { 'X-User-Email': email } : {} });
  return response.data;
};

// --- Admin review console (/admin/feedback) --------------------------------
// Admin-only; a non-admin caller gets a generic 404 server-side.

export const listFeedbackAdmin = async () => {
  const email = firebaseAuth?.currentUser?.email;
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/list-feedback`,
      { user_email: email }, { headers: email ? { 'X-User-Email': email } : {} });
    return response.data;
  } catch (error) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

// Consented feedback -> an UNAPPROVED testimonial record. Publishing is a
// separate call, so promoting can never put words on the site by itself.
export const promoteFeedback = async ({ feedbackId, displayName, quote }) => {
  const email = firebaseAuth?.currentUser?.email;
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/promote-feedback`,
      { user_email: email, feedback_id: feedbackId, display_name: displayName, quote },
      { headers: email ? { 'X-User-Email': email } : {} });
    return response.data;
  } catch (error) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const setTestimonialApproved = async ({ testimonialId, approved }) => {
  const email = firebaseAuth?.currentUser?.email;
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/approve-testimonial`,
      { user_email: email, testimonial_id: testimonialId, approved },
      { headers: email ? { 'X-User-Email': email } : {} });
    return response.data;
  } catch (error) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * PUBLIC approved testimonials: [{quote, display_name, role, context}].
 * Unauthenticated — works signed-out — mirroring getPublicFlags (approved
 * quotes are public by definition). Fails closed to [] on any error: the
 * carousel renders NOTHING rather than placeholders.
 */
export const fetchTestimonials = async () => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/get-testimonials`, {
      timeout: 10000,
    });
    const data = response.data;
    return data?.success && Array.isArray(data.testimonials) ? data.testimonials : [];
  } catch {
    return [];
  }
};


/**
 * Meta's data deletion status (#5154). Public, no sign-in: the code Meta gave
 * the person is the only key. {outcome, date, confirmation_code}, or null
 * for an unknown code or a failed read.
 */
export const fetchFacebookDeletionStatus = async (code) => {
  if (!/^[0-9a-f]{16}$/.test(String(code || ''))) return null;
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/facebook-deletion-status`, { params: { code }, timeout: 8000 });
    const data = response.data;
    return data?.success ? { outcome: data.outcome, date: data.date, confirmation_code: data.confirmation_code } : null;
  } catch {
    return null;
  }
};

/**
 * Which ways in the sign-in sheet may offer (#3928). Public, no sign-in.
 * `{email_link: bool}`, or null when the read failed (the sheet then offers
 * Google only, as before).
 */
export const fetchAuthMethods = async () => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/get-auth-methods`, { timeout: 8000 });
    const data = response.data;
    if (!data?.success) return null;
    // phone_email_first (#5084): the email-first sheet on iPhone and iPad
    // Safari, true only when the flag is on for everyone.
    // more_sign_in_options (#5154): the Apple and Facebook buttons' flag, on
    // for everyone; apple / facebook: each provider set up in Firebase Auth.
    return {
      email_link: data.email_link === true, phone_email_first: data.phone_email_first === true,
      more_sign_in_options: data.more_sign_in_options === true,
      apple: data.apple === true, facebook: data.facebook === true,
    };
  } catch {
    return null;
  }
};

/**
 * The Home Screen app's sign-in code (#4189). Public, no sign-in (it is how
 * the app signs in). Both resolve {ok: true, ...} or {ok: false, error}, where
 * error is the server's outcome word, or 'network' when the server was never
 * reached. Never reject.
 */
const codeCall = async (route, body) => {
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/${route}`, body, { timeout: 15000 });
    const data = response.data || {};
    return data.success ? { ok: true, ...data } : { ok: false, error: data.error || 'other' };
  } catch (error) {
    const data = error?.response?.data;
    if (data && typeof data === 'object') return { ok: false, error: data.error || 'other', tries_left: data.tries_left };
    return { ok: false, error: error?.response ? 'other' : 'network' };
  }
};

export const sendSignInCode = (email, carry) => codeCall('email-code-send', { email, ...(carry ? { carry } : {}) });

export const verifySignInCode = (requestId, email, code) => codeCall('email-code-verify', { request_id: requestId, email, code });

/**
 * The /got-in form (#4726, ADR 0056). Public, no sign-in, no name: called the
 * way the sign-in code routes are. Both resolve {ok: true, ...} or
 * {ok: false, error, field?}, where error is the server's outcome word
 * (invalid | under_13 | rate_limited | unavailable | not_found), or 'network'
 * when the server was never reached. Never reject.
 */
const outcomeCall = async (route, body) => {
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/${route}`, body, { timeout: 15000 });
    const data = response.data || {};
    return data.success ? { ok: true, ...data } : { ok: false, error: data.error || 'other', field: data.field };
  } catch (error) {
    const data = error?.response?.data;
    if (data && typeof data === 'object') return { ok: false, error: data.error || 'other', field: data.field };
    return { ok: false, error: error?.response ? 'other' : 'network' };
  }
};

export const submitOutcomeReport = (body) => outcomeCall('outcome-report-submit', body);

export const deleteOutcomeReport = (code) => outcomeCall('outcome-report-delete', { code });

/**
 * The landing page's Master's abroad waitlist (#4967, ADR 0059). Public: a
 * signed-out visitor types an address; a signed-in account's token rides
 * along (the axios interceptor) and the server uses its own address. Both
 * resolve {ok: true, ...} or {ok: false, error, field?} like the /got-in
 * form (error: invalid | rate_limited | unavailable | not_found, or
 * 'network'). Never reject. The flag (india_masters) is checked server-side.
 */
export const joinMastersWaitlist = (body) => outcomeCall('masters-waitlist-join', body);

/** The section, or /masters (`surface`, #4985), came on screen (counted
 *  server-side per day, no person). */
export const markMastersWaitlistSeen = (body) => outcomeCall('masters-waitlist-seen', body);

/** The US home page's line to /masters was clicked (#4985): counted
 *  server-side per day (no person) and sent as masters_link_clicked. */
export const markMastersLinkClicked = (body) => outcomeCall('masters-link-click', body);

/**
 * The rupee Season Pass (#4966, ADR 0060): a surface showed the local price,
 * or the visitor switched currency. Counted server-side per day (no person)
 * and sent as server events; dark behind india_masters. Fire and forget:
 * resolves like the waitlist's calls and never rejects.
 */
export const markRegionalPricingSeen = (body) => outcomeCall('regional-pricing-seen', body);

export const markRegionalCurrencySwitch = (body) => outcomeCall('regional-pricing-switch', body);

/**
 * Through Graduation shown and chosen on /pricing before the server knew the
 * student (#5207): signed out, or no grade on file. Public beacons, counted
 * server-side as day counts with no person in them (profile_manager_v2
 * graduation_pricing.py, dark behind one_payment_passes). Never reject.
 */
export const markGraduationPricingSeen = (body) => outcomeCall('graduation-pricing-seen', body);

export const markGraduationPricingChosen = (body) => outcomeCall('graduation-pricing-chosen', body);

/**
 * The college pages' Watch section (#4608): our live Shorts and tutorials
 * per school, {slug: [video]}. Public, no sign-in. `college` narrows it to
 * one page. `null` when the read failed (the page keeps what it has, and a
 * page with nothing renders no section).
 */
export const fetchCollegeVideos = async (college = null) => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/get-college-videos`, {
      params: college ? { college } : {}, timeout: 8000,
    });
    const data = response.data;
    if (!data?.success || !data.colleges || typeof data.colleges !== 'object') return null;
    return data.colleges;
  } catch {
    return null;
  }
};

/**
 * /videos' one read (#5433): every public video ({slug: [video]}) and, when
 * videos_top_shorts is on for someone, `top`, our most-watched Shorts
 * ({as_of, shorts}; null when the server sent none). `null` when the read
 * failed, the fetchCollegeVideos rule.
 */
export const fetchVideosPage = async () => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/get-college-videos`, {
      params: { top: '1' }, timeout: 8000,
    });
    const data = response.data;
    if (!data?.success || !data.colleges || typeof data.colleges !== 'object') return null;
    const top = data.top && typeof data.top === 'object' && Array.isArray(data.top.shorts) ? data.top : null;
    return { colleges: data.colleges, top };
  } catch {
    return null;
  }
};

/**
 * /videos' first-party play counter (#5433, profile_manager_v2
 * video_events.py): {video, event: play|complete|to_college|to_ask, play_id,
 * win, team}. Content-free day counts; dark behind videos_top_shorts. Never
 * rejects, and nothing waits on it.
 */
export const countVideoEvent = (body) => outcomeCall('video-event', body);

/**
 * The landing proof strip's figures and milestone ribbon (#3820). Public, no
 * sign-in. `null` when the read failed: the strip and the ribbon then render
 * nothing, the same as before the founder's Go live (`live: false`).
 */
export const fetchLandingStats = async () => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/get-landing-stats`, { timeout: 8000 });
    const data = response.data;
    if (!data?.success) return null;
    return {
      live: data.live === true,
      tiles: Array.isArray(data.tiles) ? data.tiles : [],
      ribbon: data.ribbon && typeof data.ribbon === 'object' ? data.ribbon : null,
      // Where visitors come from (#4607): figures and shade steps, never counts.
      reach: data.reach && typeof data.reach === 'object' ? data.reach : null,
    };
  } catch {
    return null;
  }
};


// ==================== FEATURE FLAGS (#842) ====================

/**
 * Resolved feature-flag booleans for the signed-in user. The allowlist itself
 * never reaches the client — the server resolves membership and returns only
 * booleans. Throws on error; the FeatureFlagsProvider fails closed to {}.
 */
export const getMyFlags = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/get-my-flags`, {
    params: { user_email: userEmail },
    timeout: 10000,
    headers: { 'X-User-Email': userEmail },
  });
  return response.data?.flags || {};
};

/**
 * PUBLIC feature flags: only the flags in 'on' mode (generally available).
 * Unauthenticated — works signed-out — because 'on' is public by definition;
 * allowlist/rules membership never appears here. Used to unlock a feature's
 * how-to paper/video once it launches to Everyone. Fails closed to {} at the
 * caller (an error reads as "nothing GA").
 */
export const getPublicFlags = async () => {
  const response = await axios.get(`${getProfileManagerUrl()}/get-public-flags`, {
    timeout: 10000,
  });
  return response.data?.flags || {};
};

/** Admin-only: full flag picture {flag: {mode, allowlist, description}}. */
// Every school we carry — id + official name + state, one tiny row each.
export const getUniversityDirectory = async () => {
  const base = import.meta.env.VITE_KNOWLEDGE_BASE_UNIVERSITIES_V2_URL
    || 'https://knowledge-base-manager-universities-v2-pfnwjfp26a-ue.a.run.app';
  const res = await axios.get(base, { params: { action: 'directory' }, timeout: 30000 });
  return res.data?.universities || [];
};

// KB health for the admin console: counts by cycle/verification + gap lists.
export const getKbCoverage = async () => {
  const base = import.meta.env.VITE_KNOWLEDGE_BASE_UNIVERSITIES_V2_URL
    || 'https://knowledge-base-manager-universities-v2-pfnwjfp26a-ue.a.run.app';
  const res = await axios.get(base, { params: { action: 'coverage' }, timeout: 30000 });
  return res.data || {};
};

// Admin-only: the school search-demand table (misses = KB expansion queue).
export const adminGetSearchDemand = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/admin-search-demand`, {
    params: { user_email: userEmail },
    timeout: 15000,
    headers: { 'X-User-Email': userEmail },
  });
  return response.data?.demand || [];
};

// Mark a search-demand row as noise (typo, garbled fragment, self-testing —
// founder ask 2026-07-31) so it drops out of the expansion queue. Soft
// dismiss: a genuinely new search after this resurfaces the row.
export const adminDismissSearchDemand = async (userEmail, query) => {
  const response = await axios.post(`${getProfileManagerUrl()}/admin-dismiss-search-demand`,
    { user_email: userEmail, query },
    { timeout: 15000, headers: { 'X-User-Email': userEmail } },
  );
  return response.data;
};

export const adminGetFlags = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/admin-get-flags`, {
    params: { user_email: userEmail },
    timeout: 10000,
    headers: { 'X-User-Email': userEmail },
  });
  return response.data?.flags || {};
};

// ==================== COLLABORATIVE REVIEW: SHARE CRUD (#845) ====================
// Student-owned grant lifecycle. All flag-gated server-side (404 while dark).
// createShare/updateShare may return `link` — the ONLY time the secret exists
// outside the reviewer's email; the caller caches it locally (shareScopes.js).

// Who the account is for (#5811): Settings' switch. 404 while parent_path
// is off for the account (the server's gate).
export const setAccountRole = async (userEmail, role) => {
  const response = await axios.post(`${getProfileManagerUrl()}/set-account-role`,
    { user_email: userEmail, role },
    { timeout: 15000, headers: { 'X-User-Email': userEmail } });
  return response.data;
};

export const createShare = async (userEmail, payload) => {
  const response = await axios.post(`${getProfileManagerUrl()}/create-share`,
    { user_email: userEmail, ...payload },
    { timeout: 15000, headers: { 'X-User-Email': userEmail } });
  return response.data;
};

export const listShares = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/list-shares`, {
    params: { user_email: userEmail },
    timeout: 15000,
    headers: { 'X-User-Email': userEmail },
  });
  return response.data?.shares || [];
};

export const revokeShare = async (userEmail, grantId) => {
  const response = await axios.post(`${getProfileManagerUrl()}/revoke-share`,
    { user_email: userEmail, grant_id: grantId },
    { timeout: 15000, headers: { 'X-User-Email': userEmail } });
  return response.data;
};

export const updateShare = async (userEmail, grantId, updates = {}) => {
  const response = await axios.post(`${getProfileManagerUrl()}/update-share`,
    { user_email: userEmail, grant_id: grantId, ...updates },
    { timeout: 15000, headers: { 'X-User-Email': userEmail } });
  return response.data;
};

// Owner-only: mint a short-lived preview session so the SENDER can open a
// code-protected share without the reviewer's email code (#866). The token is
// seeded into the preview link's scrubbed fragment; the guest gateway honors
// it and treats the open as a preview, not a reviewer visit.
export const createPreviewSession = async (userEmail, grantId) => {
  const response = await axios.post(`${getProfileManagerUrl()}/preview-share-session`,
    { user_email: userEmail, grant_id: grantId },
    { timeout: 15000, headers: { 'X-User-Email': userEmail } });
  return response.data?.session || null;
};

// Comments on a shared link (#4987, ADR 0062): the student's inbox, reply and
// resolve. Dark behind share_comments server-side (404 while off). The inbox
// throws on failure so a caller never reads a failed fetch as "no notes".
export const getShareComments = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/share-comments`, {
    params: { user_email: userEmail },
    timeout: 15000,
    headers: { 'X-User-Email': userEmail },
  });
  if (!response.data?.success) throw new Error('share comments unavailable');
  return response.data;
};

export const replyShareComment = async (userEmail, grantId, commentId, body) => {
  const response = await axios.post(`${getProfileManagerUrl()}/reply-share-comment`,
    { user_email: userEmail, grant_id: grantId, comment_id: commentId, body },
    { timeout: 15000, headers: { 'X-User-Email': userEmail } });
  return response.data;
};

export const resolveShareComment = async (userEmail, grantId, commentId, resolved = true) => {
  const response = await axios.post(`${getProfileManagerUrl()}/resolve-share-comment`,
    { user_email: userEmail, grant_id: grantId, comment_id: commentId, resolved },
    { timeout: 15000, headers: { 'X-User-Email': userEmail } });
  return response.data;
};

export const markShareCommentsSeen = async (userEmail, grantId, commentIds) => {
  const response = await axios.post(`${getProfileManagerUrl()}/mark-share-comments-seen`,
    { user_email: userEmail, grant_id: grantId, comment_ids: commentIds },
    { timeout: 15000, headers: { 'X-User-Email': userEmail } });
  return response.data;
};

export const removeShareComment = async (userEmail, grantId, commentId) => {
  const response = await axios.post(`${getProfileManagerUrl()}/remove-share-comment`,
    { user_email: userEmail, grant_id: grantId, comment_id: commentId },
    { timeout: 15000, headers: { 'X-User-Email': userEmail } });
  return response.data;
};


/** Admin-only: set a flag's mode ('off'|'allowlist'|'rules'|'on') and optionally
 *  its allowlist and/or rule set. */
export const adminSetFlag = async (userEmail, flag, mode, allowlist, rules) => {
  const body = { user_email: userEmail, flag, mode };
  if (allowlist !== undefined) body.allowlist = allowlist;
  if (rules !== undefined) body.rules = rules;
  const response = await axios.post(`${getProfileManagerUrl()}/admin-set-flag`, body, {
    timeout: 10000,
    headers: { 'X-User-Email': userEmail },
  });
  return response.data;
};


// ==================== ADMIN: FREE-CREDIT GRANTS ====================
// Admin pushes credits to selected users; each gets an email and claims them
// in-app. Deliberately NOT the older /add-credits route — that one is gated by
// a server-side-only secret that must never reach a browser.

export const adminListUsers = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/admin-list-users`, {
    params: { user_email: userEmail },
    timeout: 20000,
    headers: { 'X-User-Email': userEmail },
  });
  return response.data?.users || [];
};

export const adminGrantCredits = async (userEmail, emails, credits, note, mode) => {
  const response = await axios.post(`${getProfileManagerUrl()}/admin-grant-credits`,
    { user_email: userEmail, emails, credits, note, mode: mode || 'gift' },
    // Feedback mode reads each recipient's usage before writing their email
    // (~1s each, 25-recipient server cap) — an aborted request mid-batch is
    // how duplicate grants get manufactured, so the timeout leaves headroom.
    { timeout: 180000, headers: { 'X-User-Email': userEmail } });
  return response.data;
};

export const adminListGrants = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/admin-list-grants`, {
    params: { user_email: userEmail },
    timeout: 15000,
    headers: { 'X-User-Email': userEmail },
  });
  return response.data?.grants || [];
};

// Comp a subscription tier (monthly | season_pass) to selected users —
// lands immediately, no claim step, time-boxed server-side (founder ask
// 2026-07-31). Distinct from adminGrantCredits, which issues an emailed
// credit OFFER the student must claim.
export const adminGrantSubscription = async (userEmail, emails, plan, note) => {
  const response = await axios.post(`${getProfileManagerUrl()}/admin-grant-subscription`,
    { user_email: userEmail, emails, plan, note },
    { timeout: 30000, headers: { 'X-User-Email': userEmail } });
  return response.data;
};

// Major Map generation settings (model + catalogue vocabulary policy). ADMIN
// ONLY — there is no student-facing counterpart, and no student surface should
// ever call these. The GET carries a live `preview` (vocabulary size, token
// estimate, coverage) so the spend trade is made on real numbers; it reads the
// whole catalogue server-side, hence the roomier timeout.
export const adminGetMajorMapSettings = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/admin-get-major-map-settings`, {
    params: { user_email: userEmail },
    timeout: 20000,
    headers: { 'X-User-Email': userEmail },
  });
  return response.data;
};

export const adminSetMajorMapSettings = async (userEmail, settings) => {
  const response = await axios.post(`${getProfileManagerUrl()}/admin-set-major-map-settings`,
    { user_email: userEmail, ...settings },
    { timeout: 20000, headers: { 'X-User-Email': userEmail } });
  return response.data;
};

// Concierge runtime settings (daily turn caps, agentic loop budgets, agentic
// model). ADMIN ONLY — read at turn time by counselor_agent, no deploy.
export const adminGetConciergeSettings = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/admin-get-concierge-settings`, {
    params: { user_email: userEmail },
    headers: { 'X-User-Email': userEmail },
  });
  return response.data;
};

export const adminSetConciergeSettings = async (userEmail, settings) => {
  const response = await axios.post(`${getProfileManagerUrl()}/admin-set-concierge-settings`,
    { user_email: userEmail, ...settings },
    { headers: { 'X-User-Email': userEmail } });
  return response.data;
};

// Astra's journey-stage metadata (labels, priorities, first-launch intro,
// ask overlays, ladder ordering). ADMIN ONLY — read at turn time by
// counselor_agent (cached ~60s), no deploy. The test-score safety floor is
// code-side and cannot be edited from here.
export const adminGetAstraStages = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/admin-get-astra-stages`, {
    params: { user_email: userEmail },
    headers: { 'X-User-Email': userEmail },
  });
  return response.data;
};

// The turn objectives Astra is given (#2249). The text lives in Firestore so
// it is tunable without a deploy; the counselor's code defaults are the
// fail-safe, so clearing a box restores the shipping wording.
export const adminGetAstraObjectives = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/admin-get-astra-objectives`, {
    params: { user_email: userEmail },
    headers: { 'X-User-Email': userEmail },
  });
  return response.data;
};

export const adminSetAstraObjectives = async (userEmail, overrides) => {
  const response = await axios.post(`${getProfileManagerUrl()}/admin-set-astra-objectives`,
    { user_email: userEmail, ...overrides },
    { headers: { 'X-User-Email': userEmail } });
  return response.data;
};

// Astra Lite's daily limits (#2611): the console's view (values, bounds,
// the worst-case cost estimate, recent changes) and the admin write.
export const adminGetAstraLiteLimits = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/admin-get-astra-lite-limits`, {
    params: { user_email: userEmail },
    headers: { 'X-User-Email': userEmail },
  });
  return response.data;
};

export const adminSetAstraLiteLimits = async (userEmail, limits) => {
  const response = await axios.post(`${getProfileManagerUrl()}/admin-set-astra-lite-limits`,
    { user_email: userEmail, limits },
    { headers: { 'X-User-Email': userEmail } });
  return response.data;
};

// Astra ops reads: recorded live-ladder runs + field telemetry counters.
export const adminGetAstraTestRuns = async (userEmail) => {
  const response = await axios.get(`${getProfileManagerUrl()}/admin-get-astra-test-runs`, {
    params: { user_email: userEmail }, headers: { 'X-User-Email': userEmail },
  });
  return response.data;
};

export const adminSetAstraStages = async (userEmail, overrides) => {
  const response = await axios.post(`${getProfileManagerUrl()}/admin-set-astra-stages`,
    { user_email: userEmail, ...overrides },
    { headers: { 'X-User-Email': userEmail } });
  return response.data;
};

// Per-surface LLM model settings (#1205) — every other part of the app that
// calls a model, each independently selectable. ADMIN ONLY; the backend
// re-checks the allowlist and 404s otherwise. `tab` narrows the response to one
// section's surfaces, so opening a tab fetches only what that tab shows; omit
// it to get the tab strip.
export const adminGetLlmSettings = async (userEmail, tab) => {
  const response = await axios.get(`${getProfileManagerUrl()}/admin-get-llm-settings`, {
    params: { user_email: userEmail, ...(tab ? { tab } : {}) },
    headers: { 'X-User-Email': userEmail },
  });
  return response.data;
};

export const adminSetLlmSettings = async (userEmail, surface, model, tab) => {
  const response = await axios.post(`${getProfileManagerUrl()}/admin-set-llm-settings`,
    { user_email: userEmail, surface, model, tab },
    { headers: { 'X-User-Email': userEmail } });
  return response.data;
};

// The student redeeming their own grant. The backend uses the VERIFIED token
// identity, not this email — sending it only satisfies the identity gate.
export const claimCredits = async (userEmail, token) => {
  const response = await axios.post(`${getProfileManagerUrl()}/claim-credits`,
    { user_email: userEmail, token },
    { timeout: 20000, headers: { 'X-User-Email': userEmail }, validateStatus: () => true });
  return response.data || { success: false, reason: 'network' };
};

// --- Assent (compliance fix 2, 2026-08-16) ---
// The click-through record: Terms + Privacy agreement and the 13-or-older
// attestation. Identity is the VERIFIED Firebase token the axios interceptor
// attaches — no email argument, deliberately: the backend refuses anything
// but a signed-in user, so there is nothing for a caller to spoof.
export const getAssent = async () => {
  const response = await axios.get(`${getProfileManagerUrl()}/assent`,
    { timeout: 15000, validateStatus: () => true });
  return response.data || { success: false, reason: 'network' };
};

export const recordAssent = async (version) => {
  const response = await axios.post(`${getProfileManagerUrl()}/assent`,
    { version },
    { timeout: 15000, validateStatus: () => true });
  return response.data || { success: false, reason: 'network' };
};

// --- Data rights (compliance fix 3, 2026-08-16) ---
// Self-service export + deletion. Like assent: no email argument — the
// backend acts only on the VERIFIED token identity, so a caller can export
// or delete exactly themselves. Long timeouts: a full account tree walk is
// slower than a doc read. The delete's confirmation phrase is re-checked
// server-side; sending it here is the contract, not the guard.
export const exportMyData = async () => {
  const response = await axios.get(`${getProfileManagerUrl()}/export-my-data`,
    { timeout: 120000, validateStatus: () => true });
  return response.data || { success: false, reason: 'network' };
};

export const deleteMyAccount = async () => {
  const response = await axios.post(`${getProfileManagerUrl()}/delete-my-account`,
    { confirm: 'DELETE' },
    { timeout: 120000, validateStatus: () => true });
  return response.data || { success: false, reason: 'network' };
};

export default api;


// ============================================================================
// THE BUILDER TRACK (#2296) — the student's invention record. A refusal
// lands here as {success: false} like every other helper in this file, so
// the page never renders a confident empty state.
// ============================================================================
// A refusal keeps the server's whole body (audit 2026-09-23): the page reads
// `needs`, `pending`, `open_questions` and `in_progress` to say WHY, and
// keeping only the error code turned every refusal into "could not be
// requested". `status` stays the HTTP code (the page branches on 409, 429,
// 503), so the status OBJECT some refusals carry (a 409's record) moves to
// `record`.
export const builderFailure = (error) => {
  const data = error?.response?.data;
  const body = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
  const { status: record, ...rest } = body;
  return {
    ...rest,
    ...(record && typeof record === 'object' ? { record } : {}),
    success: false,
    error: body.error || 'request_failed',
    status: error?.response?.status || null,
    limit_reached: Boolean(body.limit_reached),
  };
};

const builderGet = async (path, userEmail, params = {}) => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/${path}`, {
      params: { user_email: userEmail, ...params },
      timeout: 15000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error(`[API] Error on ${path}:`, error);
    return builderFailure(error);
  }
};

// Every builder-* / pitch-* / study-* POST is a write to the record the
// sidebar's badges and the Build home read through the shared status store
// (hooks/useBuilderStatus), so each one opens a new epoch there, whether it
// succeeded or not (a failure may still have written: a 409 with the record
// in its body). Review 2026-09-27 on #2394: without this, a filing recorded
// on the patent page left Start here where it was for the whole session.
const builderPost = async (path, userEmail, body = {}, timeout = 20000) => {
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/${path}`,
      { user_email: userEmail, ...body },
      { timeout, headers: { 'X-User-Email': userEmail } });
    return response.data;
  } catch (error) {
    console.error(`[API] Error on ${path}:`, error);
    return builderFailure(error);
  } finally {
    invalidateBuilderStatus();
  }
};

export const getBuilderStatus = (userEmail) => builderGet('builder-status', userEmail);
export const getBuilderInvention = (userEmail) => builderGet('builder-invention', userEmail);
export const startBuilder = (userEmail) => builderPost('builder-start', userEmail);
export const answerBuilder = (userEmail, questionId, text) =>
  builderPost('builder-answer', userEmail, { question_id: questionId, text });
// Founder pick D: the specification's questions, answered on its card; each
// box's words are ADDED to that interview answer on the server, in one save.
// `leftOpen` = the specification's asks the student marked "not decided";
// the server records them on the round. Additions may be empty then.
export const addBuilderAnswers = (userEmail, additions, leftOpen) =>
  builderPost('builder-add-answers', userEmail, {
    additions: additions || {},
    left_open: Array.isArray(leftOpen) ? leftOpen : [],
  });
// The first draft is a billed generation; redrafts ride the daily cap.
export const draftBuilder = (userEmail) => builderPost('builder-draft', userEmail, {}, 120000);
export const addBuilderPriorArt = (userEmail, { url, title, howMineDiffers }) =>
  builderPost('builder-prior-art', userEmail, { url, title, how_mine_differs: howMineDiffers, via: 'page' });
export const removeBuilderPriorArt = (userEmail, id) =>
  builderPost('builder-prior-art', userEmail, { remove_id: id });
export const exportBuilder = (userEmail) => builderGet('builder-export', userEmail);
// The formal specification rides the queue (design 2026-09-21). The request
// answers 202 queued, 200 already ready, 402 season pass, 422 not ready, 409
// in progress, 503 queue down; the page reads status.specification after.
export const requestBuilderSpecification = (userEmail) => builderPost('builder-specification', userEmail, {});
// The prior-art search rides the same queue (design 2026-09-22). `extraTerms`
// are the student's own words for a second pass; omitted means the same words.
export const requestBuilderSearch = (userEmail, extraTerms) =>
  builderPost('builder-search', userEmail, Array.isArray(extraTerms) ? { extra_terms: extraTerms } : {});
// The family filed; the page records the receipt's two facts (ADR 0012, amended 2026-09-22).
export const recordBuilderFiling = (userEmail, applicationNumber, filingDate) =>
  builderPost('builder-filed', userEmail, { application_number: applicationNumber, filing_date: filingDate });
// Founder ask 2026-09-23: the recorded filing onto the activities and/or the
// roadmap. The server words everything from the filing record and refuses
// (400) until one is recorded; `{activity, roadmap}` choose the halves.
export const addBuilderFilingToApplication = (userEmail, { activity = true, roadmap = true } = {}) =>
  builderPost('builder-add-to-application', userEmail, { activity: Boolean(activity), roadmap: Boolean(roadmap) });
export const reviewBuilderMatch = (userEmail, matchId, decision, howMineDiffers) =>
  builderPost('builder-search-review', userEmail, { match_id: matchId, decision, how_mine_differs: howMineDiffers || '' });
// A stored artifact (the specification HTML or one of the two filing PDFs),
// as a Blob. Resolves null on any failure; the caller says so honestly.
export const downloadBuilderDocument = async (userEmail, filename) => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/download-document`, {
      params: { user_email: userEmail, filename }, responseType: 'blob', timeout: 60000,
      headers: { 'X-User-Email': userEmail },
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error on download-document:', error);
    return null;
  }
};


// ============================================================================
// THE PITCH LAB (#2358). Season-Pass-gated server-side; a refusal lands as {success: false, ...body} through the same
// builderFailure shape, so the page reads `needs`, `in_progress` and
// `limit_reached` to say why.
// ============================================================================
export const getPitchStatus = (userEmail) => builderGet('pitch-status', userEmail);
export const answerPitchQuestion = (userEmail, questionId, text) =>
  builderPost('pitch-answer', userEmail, { question_id: questionId, text });
export const savePitchQuestions = (userEmail, fields) => builderPost('pitch-save-questions', userEmail, fields || {});
// Queues the research job (#2358, 2026-09-25); the page polls pitch-status.
export const generatePitch = (userEmail) => builderPost('pitch-generate', userEmail, {}, 30000);
export const editPitchSlide = (userEmail, { slide, title, bullets, script, baseVersion }) =>
  builderPost('pitch-edit', userEmail, {
    slide, base_version: baseVersion,
    ...(title !== undefined ? { title } : {}),
    ...(bullets !== undefined ? { bullets } : {}),
    ...(script !== undefined ? { script } : {}),
  });
export const applyPitchProposals = (userEmail, runId, slides) =>
  builderPost('pitch-apply', userEmail, { run_id: runId, slides });

// A recording as base64 (audio only, capped server-side at 12 MB / six
// minutes). The review runs in the same request, so the timeout is long.
export const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '');
  reader.onerror = () => reject(reader.error || new Error('read failed'));
  reader.readAsDataURL(blob);
});

export const recordPitchRun = async (userEmail, { version, blob, mime, durationS, slideLog, onProgress }) => {
  try {
    const audioB64 = await blobToBase64(blob);
    const response = await axios.post(`${getProfileManagerUrl()}/pitch-run`, {
      user_email: userEmail, version, mime, duration_s: durationS, slide_log: slideLog, audio_b64: audioB64,
    }, {
      timeout: 300000,
      headers: { 'X-User-Email': userEmail },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    });
    return response.data;
  } catch (error) {
    console.error('[API] Error on pitch-run:', error);
    return builderFailure(error);
  }
};

// ============================================================================
// THE RESEARCH LAB (#2378, epic #2375, ADR 0017). One function per study-*
// route; Season-Pass-gated server-side, the
// Pitch Lab's shape: a refusal lands as {success: false, ...body} through
// builderFailure, so the page reads `error` (a gate's code on a 409), `needs`,
// `in_progress`, `limit_reached` and `status` (the HTTP status) to say why.
// ============================================================================
export const getStudyStatus = (userEmail) => builderGet('study-status', userEmail);
export const startStudy = (userEmail) => builderPost('study-start', userEmail, {});
export const acceptStudyCompact = (userEmail, { hoursPerWeek, venue, adultMentor, authorshipRead }) =>
  builderPost('study-compact', userEmail, {
    hours_per_week: hoursPerWeek, venue, adult_mentor: adultMentor, authorship_read: authorshipRead === true,
  });
// The mentor's area offer is a model turn: a longer timeout than a save.
// The venue, picked or changed after the compact and until the paper is
// accepted (#2379); a review under the old venue no longer counts.
export const setStudyVenue = (userEmail, venue) => builderPost('study-set-venue', userEmail, { venue });
export const proposeStudyAreas = (userEmail) => builderPost('study-propose-areas', userEmail, {}, 60000);
export const pickStudyArea = (userEmail, picked, ownWords) =>
  builderPost('study-pick-area', userEmail, { picked, own_words: ownWords });
// mode: save | add_to | rewrite (the Patent Lab's round mechanics).
export const answerStudy = (userEmail, questionId, text, mode = 'save') =>
  builderPost('study-answer', userEmail, { question_id: questionId, text, mode });
export const checkStudyQuestion = (userEmail) => builderPost('study-check-question', userEmail, {}, 60000);
export const lockStudyQuestion = (userEmail, { beforeData, howIdea, significance }) =>
  builderPost('study-lock-question', userEmail, { before_data: beforeData === true, how_idea: howIdea, significance });
// Queues the search-strategy job; the page polls study-status while it runs.
export const requestStudyPointers = (userEmail) => builderPost('study-request-pointers', userEmail, {}, 30000);
export const saveStudyReadingEntry = (userEmail, { entryId, citation, whatMeasured, whatFound, whatOpen }) =>
  builderPost('study-reading-entry', userEmail, {
    ...(entryId ? { entry_id: entryId } : {}),
    citation, what_measured: whatMeasured, what_found: whatFound, what_open: whatOpen,
  });
export const checkStudyReading = (userEmail, entryId) =>
  builderPost('study-check-reading', userEmail, { entry_id: entryId }, 60000);
export const saveStudyGap = (userEmail, text) => builderPost('study-reading-gap', userEmail, { text });
export const lockStudyReading = (userEmail) => builderPost('study-lock-reading', userEmail, {});
export const saveStudyPlan = (userEmail, { sections, approvals }) =>
  builderPost('study-plan', userEmail, {
    ...(sections ? { sections } : {}),
    ...(approvals ? { approvals } : {}),
  });
export const lockStudyPlan = (userEmail) => builderPost('study-lock-plan', userEmail, {});
export const saveStudyData = (userEmail, { summary, analysis }) =>
  builderPost('study-data', userEmail, { summary, analysis });
export const probeStudyData = (userEmail) => builderPost('study-probe-data', userEmail, {}, 60000);
export const answerStudyProbe = (userEmail, reason) =>
  builderPost('study-answer-probe', userEmail, { rejects: true, reason });
export const lockStudyData = (userEmail) => builderPost('study-lock-data', userEmail, {});
export const saveStudyPaperSection = (userEmail, section, text) =>
  builderPost('study-paper-section', userEmail, { section, text });
export const reviewStudyPaper = (userEmail) => builderPost('study-review-paper', userEmail, {}, 90000);
export const acceptStudyPaper = (userEmail) => builderPost('study-accept-paper', userEmail, {});
export const saveStudyDefenseAnswer = (userEmail, questionId, text) =>
  builderPost('study-defense-answer', userEmail, { question_id: questionId, text });
export const auditStudyDefense = (userEmail) => builderPost('study-audit-defense', userEmail, {}, 60000);
export const attestStudyContributions = (userEmail, edits) =>
  builderPost('study-contributions', userEmail, { edits: edits || {} });
export const recordStudySubmission = (userEmail, venue, date) =>
  builderPost('study-record-submission', userEmail, { venue, date });
export const recordStudyPublication = (userEmail, url) =>
  builderPost('study-record-publication', userEmail, { url }, 30000);
export const addStudyToApplication = (userEmail) => builderPost('study-add-to-application', userEmail, {});
// The interaction log as markdown ({success, markdown}); the page saves the file.
export const exportStudyLog = (userEmail) => builderGet('study-export-log', userEmail);

/**
 * First-touch acquisition onto the account record (#2997). The server keeps
 * the FIRST value it is given and refuses the rest, and only for an account
 * that already exists (a stale token after a deletion cannot recreate one).
 * Resolves {success, recorded, reason?}; never rejects.
 */
export const recordFirstTouch = async (userEmail, facts) => {
  try {
    const response = await axios.post(`${PROFILE_MANAGER_URL}/record-first-touch`, {
      user_email: userEmail,
      first_touch: facts,
    }, {
      timeout: 10000,
      headers: { 'Content-Type': 'application/json', 'X-User-Email': userEmail },
    });
    return response.data || { success: false };
  } catch (error) {
    return { success: false, error: error?.message || 'request failed' };
  }
};

// ---------------------------------------------------------------------------
// Astra Today (#3754, ADR 0041). The student's 1 to 3 moves, written by the
// server's rules. Gated server-side on astra_today: off answers 404, which
// resolves here as { success: false, off: true } so a caller never shows a
// stale "nothing needs you" for a student the feature is not on for.

/** { success, moves, list_counts, generated_at, quiet_line } or { success: false }. */
export const getAstraMoves = async (userEmail, { recompute = false } = {}) => {
  let tz = null;
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { /* old browsers */ }
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/get-astra-moves`,
      { user_email: userEmail, recompute, ...(tz ? { tz } : {}) },
      { timeout: 30000, headers: { 'X-User-Email': userEmail } });
    return response.data || { success: false };
  } catch (e) {
    return { success: false, off: e?.response?.status === 404 };
  }
};

/**
 * One answer to one move: response 'do' | 'later' | 'not_for_me' | 'reason'
 * | 'undo', option = the later choice or the reason id the move offered.
 * Resolves { success, moves } or { success: false, error }.
 */
export const respondAstraMove = async (userEmail, moveId, response, option = null) => {
  try {
    const res = await axios.post(`${getProfileManagerUrl()}/astra-move-respond`,
      { user_email: userEmail, move_id: moveId, response, ...(option ? { option } : {}) },
      { timeout: 30000, headers: { 'X-User-Email': userEmail } });
    return res.data || { success: false };
  } catch (e) {
    return { success: false, error: e?.response?.data?.error || null };
  }
};


// ---------------------------------------------------------------------------
// Astra's weekly question (#3810). Pilot-gated (404 off). Nothing writes a
// profile field until confirmAstraQuestion(..., 'yes').

const astraQPost = async (route, body) => {
  try {
    const r = await axios.post(`${getProfileManagerUrl()}/${route}`, body,
      { timeout: 30000, headers: { 'X-User-Email': body.user_email } });
    return r.data || { success: false };
  } catch (e) {
    return { success: false, error: e?.response?.data?.error || null, gone: e?.response?.status === 404 };
  }
};

/** choice_id: a chip id, 'not_now' or 'stop'; text: the student's own words. */
export const answerAstraQuestion = (userEmail, questionId, choiceId = null, text = null) =>
  astraQPost('astra-question-answer', { user_email: userEmail, question_id: questionId,
    ...(choiceId ? { choice_id: choiceId } : {}), ...(text ? { text } : {}) });

/** decision: 'yes' | 'edit' | 'no'. */
export const confirmAstraQuestion = (userEmail, questionId, decision, edited = null) =>
  astraQPost('astra-question-confirm', { user_email: userEmail, question_id: questionId, decision,
    ...(edited ? { edited } : {}) });

export const getAstraAnswers = (userEmail) => astraQPost('astra-answers', { user_email: userEmail });

export const editAstraAnswer = (userEmail, answerId, note) =>
  astraQPost('astra-answer-edit', { user_email: userEmail, answer_id: answerId, note });

export const forgetAstraAnswer = (userEmail, answerId, undo = false) =>
  astraQPost('astra-answer-forget', { user_email: userEmail, answer_id: answerId, undo });


// ---------------------------------------------------------------------------
// Notes from Astra (#3755, ADR 0041 decision 4): the ONE notification
// preference shape. Pilot-gated like the moves (404 off).

/** { success, prefs } or { success: false, off? }. */
export const getAstraNotes = async (userEmail) => {
  try {
    const response = await axios.get(`${getProfileManagerUrl()}/get-astra-notes`, {
      params: { user_email: userEmail }, timeout: 15000, headers: { 'X-User-Email': userEmail },
    });
    return response.data || { success: false };
  } catch (e) {
    return { success: false, off: e?.response?.status === 404 };
  }
};

/** Save a partial change ({kinds: {deadlines: false}}, {paused: true}, ...).
 * Resolves { success, prefs } (the effective prefs after the save) or { success: false }. */
export const saveAstraNotes = async (userEmail, patch, tz = null) => {
  try {
    const response = await axios.post(`${getProfileManagerUrl()}/save-astra-notes`,
      { user_email: userEmail, patch, ...(tz ? { tz } : {}) },
      { timeout: 15000, headers: { 'X-User-Email': userEmail } });
    return response.data || { success: false };
  } catch (e) {
    return { success: false, error: e?.response?.data?.error || null };
  }
};

// ---------------------------------------------------------------------------
// Astra Today web push (#3756, ADR 0041 decision 5). Pilot-gated (404 off).

/** { success, public_key, devices } - the VAPID public key is public by design. */
export const getPushConfig = async (userEmail) => {
  try {
    const r = await axios.get(`${getProfileManagerUrl()}/get-push-config`, {
      params: { user_email: userEmail }, timeout: 15000, headers: { 'X-User-Email': userEmail } });
    return r.data || { success: false };
  } catch (e) {
    return { success: false, off: e?.response?.status === 404 };
  }
};

export const pushSubscribe = async (userEmail, subscription, device) => {
  try {
    const r = await axios.post(`${getProfileManagerUrl()}/push-subscribe`,
      { user_email: userEmail, subscription, device }, { timeout: 15000, headers: { 'X-User-Email': userEmail } });
    return r.data || { success: false };
  } catch { return { success: false }; }
};

export const pushUnsubscribe = async (userEmail, endpoint) => {
  try {
    const r = await axios.post(`${getProfileManagerUrl()}/push-unsubscribe`,
      { user_email: userEmail, endpoint }, { timeout: 15000, headers: { 'X-User-Email': userEmail } });
    return r.data || { success: false };
  } catch { return { success: false }; }
};

/** One "notifications are on" note to the student's own devices. */
export const pushTest = async (userEmail) => {
  try {
    const r = await axios.post(`${getProfileManagerUrl()}/push-test`,
      { user_email: userEmail }, { timeout: 30000, headers: { 'X-User-Email': userEmail } });
    return r.data || { success: false };
  } catch { return { success: false }; }
};
